import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, TextInput,
  StyleSheet, Image, Modal, FlatList,
  Dimensions, ActivityIndicator, Linking, DeviceEventEmitter, RefreshControl, Platform, StatusBar,} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
// v1.12.899 — safeExpoImage : expo-image avec fallback RN Image (OTA-safe)
import { Image as ExpoImage } from '../utils/safeExpoImage';
import PremiumModal from '../components/PremiumModal';
import ChipsRow from '../components/ChipsRow';
// v1.12.161 — Massimo Dutti / COS / Sézane / Reformation supprimés du catalogue
// (doublons avec Rakuten via Awin / présence sur Sovrn auto-tracking).
import UpgradeModal from '../components/UpgradeModal';
import ProductDetailModal from '../components/ProductDetailModal';
import { AWS_CONFIG } from '../config/aws_config';
import { colors, spacing, radius, shadow } from '../theme';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { getAIUsageToday, FREE_LIMITS } from '../services/PremiumService';
import { usePremiumGuard } from '../hooks/usePremiumGuard';
import { useTheme } from '../context/ThemeContext';
import { useResetScrollOnFocus } from '../hooks/useResetScrollOnFocus';
import useResponsive from '../hooks/useResponsive';
import {
  parseSearchQuery, searchCatalog, exhaustiveSearch, suggestAutocomplete, getTrendingSearches, correctTypo,
} from '../services/SearchAgentService';
import { getIQueensProducts } from '../services/IQueensService';
import { fetchDynamicCatalog, invalidateCatalogCache } from '../services/DynamicCatalogService';
import {
  CATALOG, CATALOG_CATEGORIES, CATALOG_PARTNERS, PRICE_RANGES,
  PARTNERS_INFO,
} from '../data/catalogData';
import {
  getApprovedPrograms, searchProducts, buildAwinLink,
  AWIN_ADVERTISER_IDS,
} from '../services/AwinService';
import {
  getZalandoProducts, searchZalandoProducts, matchWithWardrobe,
} from '../services/ZalandoService';
import {
  getZaraProducts, getZaraFallback,
} from '../services/ZaraService';
import {
  applySkimlinksToProducts, buildSkimlinksUrl,
} from '../services/SkimlinksService';
import {
  getAmazonProducts, searchAmazonProducts,
} from '../services/AmazonService';
import {
  getAsosProducts, searchAsosProducts,
} from '../services/AsosService';
import { getHMProducts } from '../services/HMService';
import { mergeDuplicateProducts } from '../utils/mergeProducts';
// v1.13.43 — Registre global des images mortes (404) → masquage universel
import { markImageDead, isItemImageDead, useDeadImagesVersion } from '../utils/deadImages';
// v1.12.345 — refonte filtres catalogue : panneau unifié + compteurs dynamiques
import CatalogFiltersPanel from '../components/CatalogFiltersPanel';
import {
  computeAvailableFilters, applyFilters as applyAdvFilters,
  countActiveFilters, buildActiveChips, removeFilter as removeAdvFilter,
  loadPreferences as loadFilterPrefs, savePreferences as saveFilterPrefs,
  prefsToFilters,
} from '../services/FiltersService';
import { getSovrnAllProducts } from '../services/SovrnService';
import { isFavorite, toggleFavorite } from '../services/FavoritesService';
import {
  openProductForPurchase,
} from '../services/PurchaseTrackingService';
import { wardrobe } from '../data';
import PurchaseConfirmModal from '../components/PurchaseConfirmModal';
import TrendingBadge from '../components/TrendingBadge';
import { fetchTrendingTags, enrichProductsWithTrends, isProductTrending } from '../services/TrendingService';
import { getCurrentWeather } from '../services/WeatherService';

// v1.12.893 — saison/température courante pour priorisation catalogue
function _getSeasonNowFR() {
  const m = new Date().getMonth();
  if (m >= 2 && m <= 4)  return 'printemps';
  if (m >= 5 && m <= 7)  return 'été';
  if (m >= 8 && m <= 10) return 'automne';
  return 'hiver';
}
function _seasonMatch(ai, season) {
  const arr = ai?.seasonality;
  if (!Array.isArray(arr) || arr.length === 0) return null; // pas d'info
  const norm = arr.map(s => String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''));
  const target = season.normalize('NFD').replace(/[̀-ͯ]/g, '');
  return norm.includes(target);
}
function _tempMatch(ai, tempC) {
  if (typeof tempC !== 'number') return null;
  const range = ai?.temperatureRange;
  if (!Array.isArray(range) || range.length < 2) return null;
  const [lo, hi] = range;
  if (typeof lo !== 'number' || typeof hi !== 'number') return null;
  return tempC >= lo - 2 && tempC <= hi + 2; // tolérance 2°C
}
import { enrichCatalogWithAI } from '../services/AIStylistService';
import PartnerLogo from '../components/PartnerLogo';

const { width: W } = Dimensions.get('window');
const CARD_W = (W - spacing.xl * 2 - spacing.md) / 2;

// v1.12.205 — 'Manteaux' ajouté (long ≥ mi-cuisse) après Vestes (court < hanche)
// v1.12.294 — ajout 'Maillots' (homme + femme)
const CATS_F = ['Tout','Robes','Hauts','Vestes','Manteaux','Bas','Maillots','Chaussures','Sacs','Bijoux','Accessoires'];
const CATS_M = ['Tout','Hauts','Vestes','Manteaux','Bas','Maillots','Chaussures','Sacs','Accessoires'];
const CATS_N = ['Tout','Robes','Hauts','Vestes','Manteaux','Bas','Maillots','Chaussures','Sacs','Accessoires'];
function getCategoriesByGender(g) {
  if (g === 'M') return CATS_M;
  if (g === 'N') return CATS_N;
  return CATS_F;
}

// ═══ v1.12.20 : Filtres rapides par catégorie + genre ═══
//
// Quand l'user sélectionne une catégorie (Hauts, Bas, etc.), une 2ème barre
// d'onglets apparaît pour filtrer par sous-type (Chemises, T-shirts, etc.).
// La détection se fait via :
//   1. ai_metadata.subcategory (priorité — IA renseigne ça précisément)
//   2. Fallback sur le nom du produit (regex)
//
// Les listes sont basées sur les sous-catégories réellement présentes dans la
// DB (cf diagnostic _subcategoryDiag), avec ajout de "Chemises" qui se
// remplira quand les 658 nouveaux iqueens auront été analysés.
const SUBFILTERS = {
  Hauts: {
    // v1.12.368 — ajout Pulls (orphelins: 82 sweaters M, 94 F)
    F: ['Tout', 'Chemises', 'T-shirts', 'Pulls', 'Sweatshirts', 'Hoodies', 'Crop tops', 'Polos'],
    M: ['Tout', 'Chemises', 'T-shirts', 'Pulls', 'Sweatshirts', 'Hoodies', 'Polos'],
    N: ['Tout', 'Chemises', 'T-shirts', 'Pulls', 'Sweatshirts', 'Hoodies', 'Polos'],
  },
  Bas: {
    F: ['Tout', 'Pantalons', 'Survêtements', 'Joggings', 'Jeans', 'Jupes', 'Shorts', 'Leggings'],
    M: ['Tout', 'Pantalons', 'Survêtements', 'Joggings', 'Jeans', 'Shorts', 'Bermudas'],
    N: ['Tout', 'Pantalons', 'Survêtements', 'Joggings', 'Jeans', 'Shorts'],
  },
  Vestes: {
    // v1.12.222 — Manteaux retirés (Doudounes, Trenchs) — voir entrée Manteaux ci-dessous
    // v1.12.368 — ajout Doudounes/Imperméables/Cardigans pour M (81% orphelins) +
    //   Doudounes pour F (Moncler/padded mal couvert)
    F: ['Tout', 'Blazers', 'Bombers', 'Doudounes', 'Coupe-vent', 'Imperméables', 'Vestes ski', 'Cuir', 'Cardigans'],
    M: ['Tout', 'Blazers', 'Costumes', 'Bombers', 'Doudounes', 'Coupe-vent', 'Imperméables', 'Vestes ski', 'Cuir', 'Cardigans'],
    N: ['Tout', 'Blazers', 'Bombers', 'Doudounes', 'Cuir', 'Cardigans'],
  },
  // v1.12.222 — Manteaux SÉPARÉS de Vestes (avec sous-filtres dédiés)
  Manteaux: {
    F: ['Tout', 'Trenchs', 'Doudounes', 'Cabans', 'Parkas', 'Pardessus', 'Duffle coats', 'Imperméables'],
    M: ['Tout', 'Trenchs', 'Doudounes', 'Cabans', 'Parkas', 'Pardessus', 'Duffle coats', 'Imperméables'],
    N: ['Tout', 'Trenchs', 'Doudounes', 'Parkas', 'Imperméables'],
  },
  Chaussures: {
    // v1.12.368 — ajout Boots (orphelins: 111 boots M, 73 F)
    F: ['Tout', 'Sneakers', 'Talons', 'Bottines', 'Boots', 'Sandales', 'Mocassins', 'Ballerines'],
    M: ['Tout', 'Sneakers', 'Chelsea boots', 'Bottines', 'Boots', 'Mocassins', 'Sandales', 'Derbies'],
    N: ['Tout', 'Sneakers', 'Bottines', 'Boots', 'Mocassins', 'Sandales'],
  },
  Robes: {
    F: ['Tout', 'Courtes', 'Mi-longues', 'Longues', 'Robe chemise', 'Sans manches', 'Trapèze'],
    N: ['Tout', 'Courtes', 'Mi-longues', 'Longues', 'Robe chemise'],
  },
  Sacs: {
    all: ['Tout', 'À main', 'Bandoulière', 'À dos', 'Cabas', 'Pochettes', 'Banane'],
  },
  // v1.12.368 — Montres ajouté à Bijoux (66 orphelins là, 0 match dans Accessoires)
  Bijoux: {
    all: ['Tout', 'Bagues', 'Colliers', 'Boucles d\'oreilles', 'Bracelets', 'Pendentifs', 'Montres'],
  },
  Accessoires: {
    // v1.12.368 — Montres retiré (0 match : les montres sont catégorisées Bijoux en DB)
    all: ['Tout', 'Ceintures', 'Casquettes', 'Chapeaux', 'Foulards', 'Lunettes'],
  },
};

// Récupère la liste des sous-filtres pour une (catégorie, genre)
function getSubfiltersFor(category, gender) {
  const conf = SUBFILTERS[category];
  if (!conf) return null;
  if (conf.all) return conf.all;
  return conf[gender] || conf['F'] || conf['N'] || null;
}

// Regex de détection des sous-types par sous-filtre
// Chaque entrée renvoie true si le produit matche le sous-filtre.
// On regarde d'abord ai_metadata.subcategory (plus précis), sinon le nom.
const SUBFILTER_MATCHERS = {
  // ═══ Hauts ═══
  'Chemises': {
    sub: /\b(chemises?|chemisiers?|dress.shirts?|button.up|button.down|oxford.shirts?|blouses?)\b/i,
    name: /\b(chemises?|chemisiers?|blouses?)\b/i,
  },
  'T-shirts': {
    sub: /\b(t-?shirts?|tshirts?|tees?|d[eé]bardeurs?|tank.tops?)\b/i,
    name: /\b(t-?shirts?|tshirts?|tees?|d[eé]bardeurs?)\b/i,
  },
  'Sweatshirts': {
    sub: /\b(sweat-?shirts?|sweats?|crew.necks?)\b/i,
    name: /\b(sweat-?shirts?|sweats?)\b/i,
  },
  'Hoodies': {
    sub: /\b(hoodies?|capuches?|hooded)\b/i,
    name: /\b(hoodies?|capuches?|sweat.*capuche)\b/i,
  },
  'Crop tops': {
    sub: /\b(crop.?tops?|t-?shirt.crop|brassi[eè]res?|t-?shirts?.courts?)\b/i,
    name: /\b(crop.?tops?|brassi[eè]res?|t-?shirts?.courts?|crop)\b/i,
  },
  'Polos': {
    sub: /\b(polos?)\b/i,
    name: /\b(polos?)\b/i,
  },
  // v1.12.368 — Pulls : couvre sweater, jumper, knit, pull col rond/roulé (orphelins majeurs)
  'Pulls': {
    sub: /\b(pulls?|sweaters?|jumpers?|knits?|knitwear|knitted|crewnecks?|crew.necks?|turtlenecks?|col\s+roul[eé]|col\s+rond|pulls?\s+col|pullovers?)\b/i,
    name: /\b(pulls?|sweaters?|jumpers?|knitwear|turtlenecks?|crewnecks?|pullovers?)\b/i,
  },
  // ═══ Bas ═══
  'Pantalons': {
    // Pantalons "stylés" hors sport (chino, slim, droit...)
    // v1.12.22 : 'fluide' retiré (matchait "robe longue fluide")
    sub: /\b(pantalons?|chinos?|slims?|cargos?|bootcut|wide.leg|palazzo|carrots?|tailored|costumes?|trousers|pants)\b/i,
    nameExclude: /\b(surv[eê]tement|jogging|sport|legging|robe|dress)\b/i,
    name: /\b(pantalons?|chinos?|slims?|cargos?|trousers|pants)\b/i,
  },
  'Survêtements': {
    sub: /\b(surv[eê]tements?|track.?suits?|jogging.*surv|surv.*jogging)\b/i,
    name: /\b(surv[eê]tements?|track.?suits?)\b/i,
  },
  'Joggings': {
    sub: /\b(joggings?|joggers?|sweatpants?)\b/i,
    name: /\b(joggings?|joggers?|sweatpants?)\b/i,
  },
  'Jeans': {
    sub: /\b(jeans?|denim)\b/i,
    name: /\b(jeans?|denim)\b/i,
  },
  'Jupes': {
    sub: /\b(jupes?|skirts?|midi-jupes?|mini-jupes?|maxi-jupes?)\b/i,
    name: /\b(jupes?|skirts?)\b/i,
  },
  'Shorts': {
    sub: /\b(shorts?|short.de.sport|short.cycliste)\b/i,
    name: /\b(shorts?)\b/i,
  },
  'Bermudas': {
    sub: /\b(bermudas?|bermuda.cargo)\b/i,
    name: /\b(bermudas?)\b/i,
  },
  'Leggings': {
    sub: /\b(leggings?)\b/i,
    name: /\b(leggings?)\b/i,
  },
  // ═══ Vestes ═══
  'Blazers': {
    sub: /\b(blazers?|veste.de.tailleur|veste.tailleur)\b/i,
    name: /\b(blazers?|veste.de.tailleur|veste.tailleur)\b/i,
  },
  'Costumes': {
    sub: /\b(costumes?|veste.de.costume|suits?)\b/i,
    name: /\b(costumes?|veste.de.costume|suits?)\b/i,
  },
  'Bombers': {
    sub: /\b(bombers?|teddys?|varsity|aviateurs?)\b/i,
    name: /\b(bombers?|teddys?|aviateurs?)\b/i,
  },
  'Doudounes': {
    // v1.12.22 : "veste matelassée" inclus (les Barbour Quilted Jackets, etc.)
    // v1.12.368 : padded ajouté (16 orphelins Vestes F, ex. Moncler padded coat)
    sub: /\b(doudounes?|down.jackets?|puffers?|matelass[eé]es?|quilted|padded)\b/i,
    name: /\b(doudounes?|puffers?|matelass[eé]es?|quilted|padded\s+(jackets?|coats?))\b/i,
  },
  'Trenchs': {
    sub: /\b(trenchs?|gabardines?)\b/i,
    name: /\b(trenchs?|gabardines?)\b/i,
  },
  'Coupe-vent': {
    sub: /\b(coupe-?vents?|windbreakers?|impermeables?\sl[eé]gers?)\b/i,
    name: /\b(coupe-?vents?|windbreakers?)\b/i,
  },
  'Imperméables': {
    sub: /\b(imperm[eé]ables?|raincoats?|anoraks?)\b/i,
    name: /\b(imperm[eé]ables?|raincoats?|anoraks?)\b/i,
  },
  'Vestes ski': {
    sub: /\b(skis?|snowboards?|veste.de.ski|veste.ski)\b/i,
    name: /\b(skis?|snowboards?)\b/i,
  },
  'Cuir': {
    sub: /\b(cuirs?|leathers?|perfectos?|bikers?)\b/i,
    name: /\b(cuirs?|leathers?|perfectos?|bikers?)\b/i,
  },
  'Cardigans': {
    sub: /\b(cardigans?|gilets?)\b/i,
    name: /\b(cardigans?|gilets?)\b/i,
  },
  // v1.12.222 — Sous-filtres Manteaux
  'Cabans': {
    sub: /\b(cabans?|peacoats?|peaco?ats?)\b/i,
    name: /\b(cabans?|peacoats?)\b/i,
  },
  'Parkas': {
    sub: /\b(parkas?)\b/i,
    name: /\b(parkas?)\b/i,
  },
  'Pardessus': {
    sub: /\b(pardessus|overcoats?|topcoats?|long\s*coats?|manteaux?\s*longs?)\b/i,
    name: /\b(pardessus|overcoats?|manteaux?\s*longs?)\b/i,
  },
  'Duffle coats': {
    sub: /\b(duffle\s*coats?|dufflecoats?)\b/i,
    name: /\b(duffle\s*coats?|dufflecoats?)\b/i,
  },
  // ═══ Chaussures ═══
  'Sneakers': {
    sub: /\b(sneakers?|baskets?|trainers?|tennis)\b/i,
    name: /\b(sneakers?|baskets?|trainers?)\b/i,
  },
  'Talons': {
    sub: /\b(talons?|escarpins?|stilettos?|heels?|pumps?)\b/i,
    name: /\b(talons?|escarpins?|stilettos?|heels?)\b/i,
  },
  'Bottines': {
    // v1.12.368 : retire "boots?" standalone → maintenant capturé par "Boots"
    //   (sinon collision : 111 boots M iraient à la fois dans Bottines & Boots)
    sub: /\b(bottines?|ankle.boots?|booties)\b/i,
    name: /\b(bottines?|ankle.boots?|booties)\b/i,
  },
  // v1.12.368 — Boots générique (bottes hautes, combat, riding, ranger)
  //   exclut explicitement ce qui matche déjà Bottines/Chelsea pour éviter doublons
  'Boots': {
    sub: /\b(boots?|bottes?|combat.boots?|riding.boots?|cowboy.boots?|ranger.boots?|rain.boots?|knee.high.boots?)\b/i,
    nameExclude: /\b(ankle.boots?|chelsea.boots?|booties|bottines?)\b/i,
    name: /\b(boots?|bottes?)\b/i,
  },
  'Sandales': {
    sub: /\b(sandales?|sandals?|mules?|tongs?|claquettes?|flip.flops?)\b/i,
    name: /\b(sandales?|sandals?|mules?|tongs?|claquettes?)\b/i,
  },
  'Mocassins': {
    sub: /\b(mocassins?|loafers?|slippers?)\b/i,
    name: /\b(mocassins?|loafers?)\b/i,
  },
  'Ballerines': {
    // v1.12.368 : flats? ajouté (37 orphelins "flat shoes" F)
    sub: /\b(ballerines?|ballerinas?|ballet.flats?|slingbacks?|flats?)\b/i,
    name: /\b(ballerines?|ballet.flats?|flats?\s+shoes?|flat\s+shoes?)\b/i,
  },
  'Chelsea boots': {
    sub: /\b(chelsea.boots?|chelsea)\b/i,
    name: /\b(chelsea)\b/i,
  },
  'Derbies': {
    sub: /\b(derbies|derbys?|richelieus?|oxford)\b/i,
    name: /\b(derbies|derbys?|richelieus?|oxford)\b/i,
  },
  // ═══ Robes ═══
  'Courtes': {
    // v1.12.368 : mini dress ajouté (62 orphelins "mini" dans Robes F)
    sub: /\b(robe.courte|mini.robe|robe.mini|courtes?|robe.t-?shirt|mini\s+dress|mini.dresse?s?)\b/i,
    name: /\b(robe.courte|mini.robe|robe.mini|mini\s+dress)\b/i,
  },
  'Mi-longues': {
    sub: /\b(robe.midi|midi|mi-?longues?|mi-long)\b/i,
    name: /\b(robe.midi|midi|mi-?longues?)\b/i,
  },
  'Longues': {
    sub: /\b(robe.longue|maxi|longues?|long.dress)\b/i,
    name: /\b(robe.longue|maxi)\b/i,
  },
  'Robe chemise': {
    sub: /\b(robe.chemise|shirt.dress)\b/i,
    name: /\b(robe.chemise|shirt.dress)\b/i,
  },
  'Sans manches': {
    // v1.12.22 : "robe portefeuille" inclus (robe nouée à la taille, sans manches typique)
    sub: /\b(sans.manches|sleeveless|d[eé]bardeurs?|camisoles?|tanks?|portefeuilles?|wrap.dress)\b/i,
    name: /\b(sans.manches|sleeveless|d[eé]bardeurs?|portefeuilles?|wrap)\b/i,
  },
  'Trapèze': {
    sub: /\b(trap[eè]zes?|a-line|patineuses?)\b/i,
    name: /\b(trap[eè]zes?|patineuses?)\b/i,
  },
  // ═══ Sacs ═══
  'À main': {
    sub: /\b(sac.[aà].main|sacs?.[aà].main|handbags?|hobos?|tote.handbags?|cabas)\b/i,
    name: /\b(sac.[aà].main|sacs?.[aà].main|handbags?|hobos?)\b/i,
  },
  'Bandoulière': {
    sub: /\b(bandouli[eè]res?|crossbody|messengers?|reporters?)\b/i,
    name: /\b(bandouli[eè]res?|crossbody|messengers?|reporters?)\b/i,
  },
  'À dos': {
    sub: /\b(sac.[aà].dos|sacs?.[aà].dos|backpacks?)\b/i,
    name: /\b(sac.[aà].dos|sacs?.[aà].dos|backpacks?)\b/i,
  },
  'Cabas': {
    sub: /\b(cabas|tote.bags?|tote|shoppers?)\b/i,
    name: /\b(cabas|tote.bags?|tote|shoppers?)\b/i,
  },
  'Pochettes': {
    sub: /\b(pochettes?|clutchs?|trousses?|small.bags?)\b/i,
    name: /\b(pochettes?|clutchs?|trousses?)\b/i,
  },
  'Banane': {
    sub: /\b(banane|sac.banane|fanny.packs?|belt.bags?|waist.bags?)\b/i,
    name: /\b(banane|sac.banane|fanny.packs?)\b/i,
  },
  // ═══ Bijoux ═══
  'Bagues': {
    // v1.12.22 : bagues? pour matcher singulier ET pluriel
    sub: /\b(bagues?|rings?|chevali[eè]res?|alliances?|solitaires?)\b/i,
    name: /\b(bagues?|rings?|chevali[eè]res?|alliances?)\b/i,
  },
  'Colliers': {
    sub: /\b(colliers?|necklaces?)\b/i,
    name: /\b(colliers?|necklaces?)\b/i,
  },
  "Boucles d'oreilles": {
    // v1.12.22 : BUG REGEX FIXÉ — \\b(boucle)\\b ne matchait PAS "boucles"
    // (33 "boucles d'oreilles puces" perdues). Solution : boucles? + ajout
    // explicite de "puces" et "pendantes".
    sub: /\b(boucles?|earrings?|cr[eé]oles|piercings?|puces?|pendantes?)\b/i,
    name: /\b(boucles?|earrings?|cr[eé]oles|puces?|pendantes?)\b/i,
  },
  'Bracelets': {
    sub: /\b(bracelets?|joncs?|manchettes?|bangles?|cuffs?)\b/i,
    name: /\b(bracelets?|joncs?|manchettes?)\b/i,
  },
  'Pendentifs': {
    sub: /\b(pendentifs?|pendants?|charms?|m[eé]dailles?|broches?)\b/i,
    name: /\b(pendentifs?|pendants?|charms?)\b/i,
  },
  // ═══ Accessoires ═══
  // Note : montres sont parfois en Bijoux dans la DB (89 items), mais l'UX
  // attend de les trouver sous Accessoires. On les détecte ici aussi.
  'Montres': {
    sub: /\b(montres?|watch(es)?|chronographes?)\b/i,
    name: /\b(montres?|watch(es)?|chronographes?)\b/i,
  },
  'Ceintures': {
    sub: /\b(ceintures?|belts?)\b/i,
    name: /\b(ceintures?|belts?)\b/i,
  },
  'Casquettes': {
    sub: /\b(casquettes?|caps?|truckers?|baseball)\b/i,
    name: /\b(casquettes?|caps?|truckers?)\b/i,
  },
  'Chapeaux': {
    sub: /\b(chapeaux?|buckets?|bobs?|panamas?|fedoras?|hats?|bonnets?)\b/i,
    name: /\b(chapeaux?|buckets?|bobs?|panamas?|fedoras?|hats?|bonnets?)\b/i,
  },
  'Foulards': {
    sub: /\b(foulards?|[eé]charpes?|scarfs?|bandanas?)\b/i,
    name: /\b(foulards?|[eé]charpes?|scarfs?)\b/i,
  },
  'Lunettes': {
    sub: /\b(lunettes|sunglasses|eyewear)\b/i,
    name: /\b(lunettes|sunglasses|eyewear)\b/i,
  },
};

// Détecte si un produit matche un sous-filtre
function matchesSubfilter(product, subfilter) {
  if (!subfilter || subfilter === 'Tout') return true;
  const matcher = SUBFILTER_MATCHERS[subfilter];
  if (!matcher) return true; // sous-filtre inconnu → on laisse passer

  // 1. Priorité : ai_metadata.subcategory (l'IA est précise)
  const subcat = product?.ai_metadata?.subcategory;
  if (typeof subcat === 'string' && subcat.length > 0) {
    if (matcher.sub.test(subcat)) {
      // Exclusion possible (ex: "Pantalons" exclut survêtements)
      if (matcher.nameExclude && matcher.nameExclude.test(subcat)) return false;
      return true;
    }
    // Si subcategory existe mais ne matche pas → le produit n'est pas de ce sous-type
    // (l'IA a déjà classifié, on lui fait confiance)
    return false;
  }

  // 2. Fallback : nom du produit (pour les items sans IA encore)
  const name = (product?.name || product?.title || '').toLowerCase();
  if (!name) return false;
  if (matcher.nameExclude && matcher.nameExclude.test(name)) return false;
  return matcher.name.test(name);
}

/* ── ProductDetailModal — utilise le composant centralisé de /components ──
   (Avant : version locale obsolète sans galerie/tailles/back button) */


export default function CatalogScreen({ navigation, route }) {
  const { dark, theme } = useTheme();
  // v1.12.345 — sidebar persistante sur web ≥ 768, modal bottom-sheet sinon
  const { isDesktopWeb } = useResponsive();
  const { user, getValidToken } = useAuth();
  const { t, lang } = useI18n();
  const { showPremium, limitInfo, guardCheck, closePremium } = usePremiumGuard(user);

  // ═══ i18n maps : labels affichés pour les IDs internes (qui restent FR) ═══
  // Les IDs internes des catégories et sous-filtres sont en FR ('Tout', 'Robes',
  // 'T-shirts', etc.) car utilisés dans les data partners et regex matchers.
  // Ces maps fournissent le label traduit selon la langue active.
  const CAT_LABEL_KEY = {
    'Tout': 'cat_all',
    'Robes': 'cat_dresses',
    'Hauts': 'cat_tops',
    'Vestes': 'cat_jackets',
    'Manteaux': 'cat_coats',
    'Bas': 'cat_bottoms',
    'Maillots': 'cat_swimwear', // v1.12.303 — fix : était affiché "Tout" car clé manquante
    'Chaussures': 'cat_shoes',
    'Sacs': 'cat_bags',
    'Bijoux': 'cat_jewelry',
    'Accessoires': 'cat_accessories',
  };
  const labelForCat = (id) => t(CAT_LABEL_KEY[id] || 'cat_all');

  const SUB_LABEL_KEY = {
    'Tout': 'sub_all',
    // Hauts
    'Chemises': 'sub_shirts', 'T-shirts': 'sub_tshirts', 'Pulls': 'sub_pulls',
    'Sweatshirts': 'sub_sweatshirts',
    'Hoodies': 'sub_hoodies', 'Crop tops': 'sub_crop_tops', 'Polos': 'sub_polos',
    // Bas
    'Pantalons': 'sub_pants', 'Survêtements': 'sub_tracksuits', 'Joggings': 'sub_joggings',
    'Jeans': 'sub_jeans', 'Jupes': 'sub_skirts', 'Shorts': 'sub_shorts',
    'Bermudas': 'sub_bermudas', 'Leggings': 'sub_leggings',
    // Vestes
    'Blazers': 'sub_blazers', 'Costumes': 'sub_suits', 'Bombers': 'sub_bombers',
    'Doudounes': 'sub_puffers', 'Trenchs': 'sub_trenches', 'Coupe-vent': 'sub_windbreaker',
    'Imperméables': 'sub_raincoat', 'Vestes ski': 'sub_ski_jacket', 'Cuir': 'sub_leather',
    'Cardigans': 'sub_cardigans',
    // Manteaux
    'Cabans': 'sub_peacoats', 'Parkas': 'sub_parkas', 'Pardessus': 'sub_overcoats',
    'Duffle coats': 'sub_duffle',
    // Chaussures
    'Sneakers': 'sub_sneakers', 'Talons': 'sub_heels', 'Bottines': 'sub_ankle_boots',
    'Boots': 'sub_boots',
    'Sandales': 'sub_sandals', 'Mocassins': 'sub_loafers', 'Ballerines': 'sub_flats',
    'Chelsea boots': 'sub_chelsea', 'Derbies': 'sub_derbies',
    // Robes
    'Courtes': 'sub_short_dress', 'Mi-longues': 'sub_midi_dress', 'Longues': 'sub_long_dress',
    'Robe chemise': 'sub_shirt_dress', 'Sans manches': 'sub_sleeveless', 'Trapèze': 'sub_aline',
    // Sacs
    'À main': 'sub_handbag', 'Bandoulière': 'sub_crossbody', 'À dos': 'sub_backpack',
    'Cabas': 'sub_tote', 'Pochettes': 'sub_clutch', 'Banane': 'sub_belt_bag',
    // Bijoux
    'Bagues': 'sub_rings', 'Colliers': 'sub_necklaces', "Boucles d'oreilles": 'sub_earrings',
    'Bracelets': 'sub_bracelets', 'Pendentifs': 'sub_pendants',
    // Accessoires
    'Montres': 'sub_watches', 'Ceintures': 'sub_belts', 'Casquettes': 'sub_caps',
    'Chapeaux': 'sub_hats', 'Foulards': 'sub_scarves', 'Lunettes': 'sub_glasses',
  };
  const labelForSub = (id) => t(SUB_LABEL_KEY[id] || 'sub_all');
  // Scroll en haut quand on revient sur cet écran (FlatList)
  const scrollRef = useResetScrollOnFocus({ isList: true });
  // useState pour userGender — ne change QUE si le genre change vraiment
  const [userGender, setUserGender] = useState(user?.gender || 'F');
  const [iqueensProducts, setIqueensProducts]     = useState([]);
  const [cashmereProducts, setCashmereProducts]     = useState([]);
  // ═══ v1.7.81 : produits TOUS depuis partner_products (cron Lambda fr-fr) ═══
  const [tousProducts,     setTousProducts]         = useState([]);
  // v1.12.167 — Rakuten supprimé (qualité catalogue Marketplace insuffisante)
  const rakutenProducts = [];
  // v1.12.294 — Suit Negozi via Rakuten LinkShare (cron rakuten-catalog-cron)
  const [suitNegoziProducts, setSuitNegoziProducts] = useState([]);
  // v1.12.322 — partenaire Ann Demeulemeester
  const [annDemeulemeesterProducts, setAnnDemeulemeesterProducts] = useState([]);
  // v1.12.336 — partenaire COUTR (USA luxe, prix USD)
  const [coutrProducts, setCoutrProducts] = useState([]);
  // v1.12.862 — partenaire DRESTIGE (Awin luxe homme + femme, 13% commission)
  const [drestigeProducts, setDrestigeProducts] = useState([]);
  // v1.12.877 — partenaire TAMARIS (Tradedoubler 6% commission, chaussures F)
  const [tamarisProducts, setTamarisProducts] = useState([]);
  // v1.12.944 — partenaire DESIGUAL (Tradedoubler 7,70%, prêt-à-porter H+F)
  const [desigualProducts, setDesigualProducts] = useState([]);
  // ═══ v1.7.83 : Diagnostic visible TOUS ═══
  // Stocke ce qui s'est passé au chargement pour afficher dans l'app
  // (puisque les logs RN ne ressortent pas dans Console iPhone d'Apple).
  const [tousDebug, setTousDebug] = useState({ status: 'loading', message: 'Chargement...', count: 0, sample: null });
  const [dynamicProducts,  setDynamicProducts]      = useState([]);
  const [dynamicLoading,   setDynamicLoading]       = useState(false);
  const [dynamicProgress,  setDynamicProgress]      = useState(0);
  const [dynamicLoaded,    setDynamicLoaded]         = useState(false);

  // ═══ v1.12.33 : refreshTrigger pour forcer rechargement ═══
  // À chaque pull-to-refresh, on incrémente cet état.
  // Tous les useEffect de chargement de catalogue dépendent de ce trigger
  // donc se relancent après invalidation du cache.
  // Aussi utilisé pour l'auto-refresh quand un écart de comptes est détecté
  // (voir useEffect "compare counts" plus bas).
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // v1.12.893 — saison + température courante pour priorisation
  const [currentSeason] = useState(_getSeasonNowFR());
  const [currentTempC, setCurrentTempC] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const w = await getCurrentWeather();
        if (!cancelled && typeof w?.temperature === 'number') setCurrentTempC(w.temperature);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Charger le catalogue dynamique au montage (scraping + cache 24h)
  useEffect(() => {
    setDynamicLoading(true);
    setDynamicProgress(0);
    fetchDynamicCatalog(userGender, (progress) => setDynamicProgress(progress))
      .then(items => {
        if (items.length > 0) {
          setDynamicProducts(items);
          // v1.12.366 / v1.13.46 — iQueens SUPPRIMÉ de l'app (sur demande) : on
          //   ignore ces produits même s'ils remontent du fetch dynamic.
          const iq = [];
          const cb = items.filter(p => p.partner === 'cashmere');
          setIqueensProducts(iq);
          setCashmereProducts(cb);
          // ═══ Ping impressions CJ pour les advertisers affichés ═══
          // Sans impressions, CJ ignore les clics. À envoyer à chaque rendu.
          try {
            const { pingCJImpression } = require('../services/CJProductsService');
            if (iq.length > 0) pingCJImpression('15830678'); // iQueens
            if (cb.length > 0) pingCJImpression('10471851'); // Cashmere
          } catch {}
        } else {
          // Pas de fallback : si scraping échoue, listes vides (pas de faux articles)
          setIqueensProducts([]);
          setCashmereProducts([]);
        }
        setDynamicLoaded(true);
      })
      .catch(() => {
        setIqueensProducts([]);
        setCashmereProducts([]);
        setDynamicLoaded(true);
      })
      .finally(() => setDynamicLoading(false));
  }, [userGender, refreshTrigger]);

  // v1.13.33 — Chargement INITIAL RAPIDE : un seul appel /catalog (tous
  // partenaires mélangés, depuis la table partner_products = "cache backend")
  // affiché tout de suite → catalogue jamais vide au 1er login/signup. Les
  // effets par partenaire (TOUS, Tamaris, …) enrichissent ensuite (dédup par id).
  useEffect(() => {
    let cancelled = false;
    const CACHE_KEY = `outfit_catalog_initial_${userGender || 'any'}`;
    let AS = null;
    try { AS = require('@react-native-async-storage/async-storage').default; } catch {}
    const norm = (items) => (items || []).map(p => ({
      id:           p.id,
      name:         p.name,
      price:        Number(p.price) || 0,
      image:        p.image_url || p.image,
      image_url:    p.image_url || p.image,
      affiliateUrl: p.affiliateUrl || p.affiliate_url || p.url,
      url:          p.affiliateUrl || p.affiliate_url || p.url,
      originalUrl:  p.affiliateUrl || p.affiliate_url || p.url,
      partner:      p.partner || 'other',
      category:     p.category || null,
      brand:        p.brand || p.partner || '',
      gender:       p.gender || p.ai_metadata?.gender || 'unisex',
      available:    p.available !== false,
      ai_metadata:  p.ai_metadata || null,
    }));
    // Fusion append + dédup par id/image (ne retire jamais ce qui est déjà affiché)
    const mergeInto = (prev, add) => {
      const seen = new Set((prev || []).map(x => String(x?.id || x?.image_url || '')));
      const extra = (add || []).filter(x => {
        const k = String(x?.id || x?.image_url || '');
        if (!k || seen.has(k)) return false; seen.add(k); return true;
      });
      return extra.length ? [...prev, ...extra] : prev;
    };
    (async () => {
      // 1. CACHE INSTANTANÉ : jamais vide à l'entrée (affiche le catalogue de la
      //    session précédente immédiatement), puis on rafraîchit en dessous.
      try {
        if (AS) {
          const raw = await AS.getItem(CACHE_KEY);
          if (raw && !cancelled) {
            const cached = JSON.parse(raw);
            if (Array.isArray(cached) && cached.length) setInitialCatalog(cached);
          }
        }
      } catch {}
      try {
        const { getCatalog } = require('../services/DatabaseService');
        // 2. LOT RAPIDE (60) → des articles s'affichent quasi immédiatement.
        const fast = await getCatalog({ limit: 60, available: true, gender: userGender });
        if (!cancelled && Array.isArray(fast) && fast.length) {
          const nf = norm(fast);
          setInitialCatalog(prev => mergeInto(prev, nf));
        }
        // 3. LOT COMPLET (400) EN PARALLÈLE → complète les articles + met en cache.
        const full = await getCatalog({ limit: 400, available: true, gender: userGender });
        if (!cancelled && Array.isArray(full) && full.length) {
          const nfull = norm(full);
          setInitialCatalog(prev => mergeInto(prev, nfull));
          try { if (AS) await AS.setItem(CACHE_KEY, JSON.stringify(nfull.slice(0, 200))); } catch {}
        }
      } catch (_) { /* silencieux : les loads par partenaire prennent le relais */ }
    })();
    return () => { cancelled = true; };
  }, [userGender, refreshTrigger]);

  // ═══ v1.7.81 : Chargement produits TOUS depuis /catalog backend ═══
  // Le cron Lambda outfit-tous-catalog-cron sync chaque nuit tous.com/fr-fr
  // dans la table partner_products. On charge ici via getCatalog({partner:'tous'}).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getCatalog } = require('../services/DatabaseService');
        // ═══ v1.9.6 : limit augmenté de 500 à 1500 ═══
        // La DB contient ~1300 produits TOUS et grandit. Lambda plafonne à 2000.
        const items = await getCatalog({ partner: 'tous', limit: 1500, available: true });
        if (cancelled) return;

        // ═══ v1.7.83 : Diagnostic détaillé pour comprendre où ça coince ═══
        const rawCount = Array.isArray(items) ? items.length : 0;
        console.log(`[CATALOG-TOUS] API returned ${rawCount} items`);

        if (rawCount === 0) {
          setTousDebug({
            status: 'empty_api',
            message: 'API /catalog?partner=tous a retourné 0 produit. Le cron Lambda n\'a pas encore inséré de produits TOUS dans la DB.',
            count: 0,
            sample: null,
          });
          setTousProducts([]);
          return;
        }

        // v1.12.145 — log "Sample raw" supprime (verbose + expose buy_url).
        // sample reste defini pour le debug box (lignes ~554+) mais n'est plus log.
        const sample = items[0];
        const normalized = items.map(p => ({
          id:           p.id,
          name:         p.name,
          price:        Number(p.price) || 0,
          image:        p.image_url || p.image,
          image_url:    p.image_url || p.image,
          affiliateUrl: p.affiliateUrl || p.affiliate_url,
          url:          p.affiliateUrl || p.affiliate_url,
          originalUrl:  p.affiliateUrl || p.affiliate_url,
          partner:      'tous',
          category:     p.category || 'Bijoux',
          brand:        p.brand || 'TOUS',
          // v1.10.69 : TOUS est principalement une marque de bijoux destinée
          // aux femmes, mais beaucoup de produits sont marqués "unisex" côté
          // backend. Pour que tous les TOUS apparaissent quel que soit le
          // genre du user :
          //   - User F : on force gender=F (voit tout TOUS)
          //   - User M : on force gender=unisex (voit aussi tout TOUS via le
          //     filtre [userGender, 'unisex'])
          gender:       userGender === 'M' ? 'unisex' : 'F',
          available:    p.available !== false,
          ai_metadata:  p.ai_metadata || null,
        }));

        // Vérifier qu'on a bien les 3 champs critiques
        const validForFilter = normalized.filter(p => {
          const hasImage = p.image && /^https?:\/\//.test(p.image);
          const hasLink  = (p.affiliateUrl || p.url || p.originalUrl) && /^https?:\/\//.test(p.affiliateUrl || p.url || p.originalUrl);
          const hasPrice = p.price > 0;
          return hasImage && hasLink && hasPrice;
        });

        const stats = {
          total: normalized.length,
          valid: validForFilter.length,
          missingImage: normalized.filter(p => !p.image || !/^https?:\/\//.test(p.image)).length,
          missingLink:  normalized.filter(p => !(p.affiliateUrl || p.url) || !/^https?:\/\//.test(p.affiliateUrl || p.url || '')).length,
          missingPrice: normalized.filter(p => !(p.price > 0)).length,
        };
        console.log(`[CATALOG-TOUS] Stats:`, JSON.stringify(stats));

        if (validForFilter.length === 0) {
          // On a reçu des produits mais aucun ne passe le filtre
          let issue = '';
          if (stats.missingLink > 0) {
            issue = `${stats.missingLink}/${stats.total} produits sans affiliate_url. La Lambda principale n'a pas été redéployée (SQL extract description.buy_url manquant).`;
          } else if (stats.missingImage > 0) {
            issue = `${stats.missingImage}/${stats.total} produits sans image. Le scraping a mal extrait les images.`;
          } else if (stats.missingPrice > 0) {
            issue = `${stats.missingPrice}/${stats.total} produits sans prix.`;
          }
          setTousDebug({
            status: 'filtered_out',
            message: `${stats.total} produits reçus, 0 valides. ${issue}`,
            count: stats.total,
            sample: {
              name: sample?.name,
              price: sample?.price,
              image_url: sample?.image_url,
              affiliate_url: sample?.affiliate_url,
              has_affiliate: !!(sample?.affiliate_url || sample?.affiliateUrl),
            },
          });
        } else {
          setTousDebug({
            status: 'ok',
            message: `${validForFilter.length}/${stats.total} produits TOUS valides`,
            count: validForFilter.length,
            sample: { name: sample?.name, price: sample?.price },
          });
        }

        setTousProducts(normalized);
      } catch (e) {
        console.warn('[CATALOG-TOUS] error:', e?.message);
        if (!cancelled) {
          setTousDebug({
            status: 'error',
            message: `Erreur API : ${e?.message || 'inconnu'}`,
            count: 0,
            sample: null,
          });
          setTousProducts([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [userGender, refreshTrigger]);

  // v1.12.167 — Rakuten loader supprime
  // v1.12.294 — chargement Suit Negozi depuis partner_products (cron rakuten-catalog-cron)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getCatalog } = require('../services/DatabaseService');
        const items = await getCatalog({ partner: 'suit_negozi', limit: 1500, available: true });
        if (cancelled) return;
        const normalized = (items || []).map(p => ({
          id:            p.id,
          name:          p.name,
          price:         Number(p.price) || 0,
          image:         p.image_url || p.image,
          image_url:     p.image_url || p.image,
          affiliateUrl:  p.affiliateUrl || p.affiliate_url,
          affiliate_url: p.affiliateUrl || p.affiliate_url,
          partner:       'suit_negozi',
          brand:         p.brand,
          category:      p.category,
          ai_metadata:   p.ai_metadata,
          discount:      p.discount,
          original_price:p.original_price,
        }));
        console.log(`[CATALOG-SUIT-NEGOZI] ${normalized.length} produits charges`);
        setSuitNegoziProducts(normalized);
      } catch (e) {
        console.warn('[CATALOG-SUIT-NEGOZI] error:', e?.message);
        if (!cancelled) setSuitNegoziProducts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [userGender, refreshTrigger]);

  // v1.12.322 — chargement Ann Demeulemeester depuis partner_products
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getCatalog } = require('../services/DatabaseService');
        const items = await getCatalog({ partner: 'ann_demeulemeester', limit: 500, available: true });
        if (cancelled) return;
        const normalized = (items || []).map(p => ({
          id:            p.id,
          name:          p.name,
          price:         Number(p.price) || 0,
          image:         p.image_url || p.image,
          image_url:     p.image_url || p.image,
          affiliateUrl:  p.affiliateUrl || p.affiliate_url,
          affiliate_url: p.affiliateUrl || p.affiliate_url,
          partner:       'ann_demeulemeester',
          brand:         p.brand,
          category:      p.category,
          ai_metadata:   p.ai_metadata,
          discount:      p.discount,
          original_price:p.original_price,
        }));
        console.log(`[CATALOG-ANN-DEMEULEMEESTER] ${normalized.length} produits charges`);
        setAnnDemeulemeesterProducts(normalized);
      } catch (e) {
        console.warn('[CATALOG-ANN-DEMEULEMEESTER] error:', e?.message);
        if (!cancelled) setAnnDemeulemeesterProducts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [userGender, refreshTrigger]);

  // v1.12.336 — chargement COUTR depuis partner_products (prix USD)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Préload du taux USD→EUR (utilisé pour le cashback COUTR)
        try { const { getUsdToEur } = require('../services/ExchangeRateService'); await getUsdToEur(); } catch {}
        const { getCatalog } = require('../services/DatabaseService');
        const items = await getCatalog({ partner: 'coutr', limit: 2000, available: true });
        if (cancelled) return;
        const normalized = (items || []).map(p => {
          // Currency depuis description JSON
          let currency = 'EUR';
          try { const desc = typeof p.description === 'string' ? JSON.parse(p.description) : p.description; currency = desc?.currency || 'USD'; } catch { currency = 'USD'; }
          return {
            id:            p.id,
            name:          p.name,
            price:         Number(p.price) || 0,
            currency,
            image:         p.image_url || p.image,
            image_url:     p.image_url || p.image,
            affiliateUrl:  p.affiliateUrl || p.affiliate_url,
            affiliate_url: p.affiliateUrl || p.affiliate_url,
            partner:       'coutr',
            brand:         p.brand,
            category:      p.category,
            ai_metadata:   p.ai_metadata,
            discount:      p.discount,
            original_price:p.original_price,
          };
        });
        console.log(`[CATALOG-COUTR] ${normalized.length} produits charges (USD)`);
        setCoutrProducts(normalized);
      } catch (e) {
        console.warn('[CATALOG-COUTR] error:', e?.message);
        if (!cancelled) setCoutrProducts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [userGender, refreshTrigger]);

  // v1.12.862 — chargement DRESTIGE depuis partner_products (Awin luxe H+F)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getCatalog } = require('../services/DatabaseService');
        const gender = userGender || undefined;
        const items = await getCatalog({ partner: 'drestige', limit: 2000, available: true, gender });
        if (cancelled) return;
        const normalized = (items || []).map(p => ({
          id:            p.id,
          name:          p.name,
          price:         Number(p.price) || 0,
          currency:      'EUR',
          image:         p.image_url || p.image,
          image_url:     p.image_url || p.image,
          affiliateUrl:  p.affiliateUrl || p.affiliate_url,
          affiliate_url: p.affiliateUrl || p.affiliate_url,
          partner:       'drestige',
          brand:         p.brand,
          category:      p.category,
          ai_metadata:   p.ai_metadata,
          discount:      p.discount,
          original_price:p.original_price,
          gender:        p.gender || p.ai_metadata?.gender,
        }));
        console.log(`[CATALOG-DRESTIGE] ${normalized.length} produits charges`);
        setDrestigeProducts(normalized);
      } catch (e) {
        console.warn('[CATALOG-DRESTIGE] error:', e?.message);
        if (!cancelled) setDrestigeProducts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [userGender, refreshTrigger]);

  // v1.12.877 — chargement TAMARIS depuis partner_products (Tradedoubler 6%)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getCatalog } = require('../services/DatabaseService');
        const gender = userGender || undefined;
        const items = await getCatalog({ partner: 'tamaris', limit: 2000, available: true, gender });
        if (cancelled) return;
        const normalized = (items || []).map(p => ({
          id:            p.id,
          name:          p.name,
          price:         Number(p.price) || 0,
          currency:      'EUR',
          image:         p.image_url || p.image,
          image_url:     p.image_url || p.image,
          affiliateUrl:  p.affiliateUrl || p.affiliate_url,
          affiliate_url: p.affiliateUrl || p.affiliate_url,
          partner:       'tamaris',
          brand:         p.brand,
          category:      p.category,
          ai_metadata:   p.ai_metadata,
          discount:      p.discount,
          original_price:p.original_price,
          gender:        p.gender || p.ai_metadata?.gender,
        }));
        console.log(`[CATALOG-TAMARIS] ${normalized.length} produits charges`);
        setTamarisProducts(normalized);
      } catch (e) {
        console.warn('[CATALOG-TAMARIS] error:', e?.message);
        if (!cancelled) setTamarisProducts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [userGender, refreshTrigger]);

  // v1.12.944 — chargement DESIGUAL depuis partner_products (Tradedoubler 7,70%)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getCatalog } = require('../services/DatabaseService');
        const gender = userGender || undefined;
        const items = await getCatalog({ partner: 'desigual', limit: 2000, available: true, gender });
        if (cancelled) return;
        const normalized = (items || []).map(p => ({
          id:            p.id,
          name:          p.name,
          price:         Number(p.price) || 0,
          currency:      'EUR',
          image:         p.image_url || p.image,
          image_url:     p.image_url || p.image,
          affiliateUrl:  p.affiliateUrl || p.affiliate_url,
          affiliate_url: p.affiliateUrl || p.affiliate_url,
          partner:       'desigual',
          brand:         p.brand,
          category:      p.category,
          ai_metadata:   p.ai_metadata,
          discount:      p.discount,
          original_price:p.original_price,
          gender:        p.gender || p.ai_metadata?.gender,
        }));
        console.log(`[CATALOG-DESIGUAL] ${normalized.length} produits charges`);
        setDesigualProducts(normalized);
      } catch (e) {
        console.warn('[CATALOG-DESIGUAL] error:', e?.message);
        if (!cancelled) setDesigualProducts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [userGender, refreshTrigger]);

  // ═══ v1.12.33 : Auto-refresh si écart serveur ↔ cache ═══
  // Au montage et après chaque chargement, on demande au backend les comptes
  // par partenaire (endpoint léger /catalog/counts). Si ça révèle un écart
  // significatif (>50 items ET >15%) avec ce qu'on a en cache, on force
  // un refresh automatique.
  // Évite que l'user reste bloqué avec un cache obsolète après un sync DB.
  useEffect(() => {
    if (!dynamicLoaded) return; // attendre que le chargement initial soit fini
    if (refreshing) return;     // pas de double-trigger pendant un pull-to-refresh
    let cancelled = false;
    (async () => {
      try {
        const { fetchCatalogCounts, detectCatalogStaleness } =
          await import('../services/DynamicCatalogService');
        const serverCounts = await fetchCatalogCounts();
        if (cancelled || !serverCounts) return;
        // Combiner tout ce qu'on a en cache (iqueens + cashmere + tous)
        const allCached = [
          ...iqueensProducts,
          ...cashmereProducts,
          ...tousProducts,
        ];
        const stale = detectCatalogStaleness(userGender, serverCounts, allCached);
        if (stale.isStale) {
          console.log('[CatalogScreen] 🔄 Auto-refresh — écart détecté:', stale.summary);
          console.log('[CatalogScreen]    deltas:', JSON.stringify(stale.deltas));
          // Vider le cache + retrigger les useEffect
          const { invalidateCatalogCache } =
            await import('../services/DynamicCatalogService');
          await invalidateCatalogCache();
          if (!cancelled) setRefreshTrigger(t => t + 1);
        }
      } catch (e) {
        console.warn('[CatalogScreen] auto-refresh check failed:', e?.message);
      }
    })();
    return () => { cancelled = true; };
    // dynamicLoaded comme déclencheur, refreshTrigger ignoré sinon boucle infinie
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicLoaded, userGender]);

  // Popup si les DEUX conditions sont réunies : 15 articles ET 5 IA
  useEffect(() => {
    if (!user?.id) return;
    getAIUsageToday(user.id).then(async aiCount => {
      // Charger le nombre d'articles du dressing
      const { getWardrobe } = require('../services/DatabaseService');
      const { getLimits } = require('../services/PremiumService');
      try {
        const [wardrobe, limits] = await Promise.all([
          getWardrobe(user.id, user?.accessToken),
          getLimits(user),
        ]);
        const wardrobeCount = Array.isArray(wardrobe) ? wardrobe.length : 0;
        const maxWardrobe = limits?.wardrobe_items ?? FREE_LIMITS.wardrobe_items;
        const maxAI       = limits?.ai_requests    ?? FREE_LIMITS.ai_requests;
        // Si limites finies atteintes → popup premium
        // FIX 2026-04 : exiger wardrobeCount > 0 (et aiCount > 0) pour éviter
        // le popup quand tout est vide (cas nouveau user free).
        if (isFinite(maxWardrobe) && isFinite(maxAI) &&
            wardrobeCount > 0 && aiCount > 0 &&
            wardrobeCount >= maxWardrobe && aiCount >= maxAI) {
          guardCheck('wardrobe', wardrobeCount, true);
        }
      } catch {}
    });
  }, [user?.id]);

  useEffect(() => {
    if (user?.gender && user.gender !== userGender) {
      setUserGender(user.gender);
    }
  }, [user?.gender]); // eslint-disable-line

  // ═══ Filtre genre pour utilisateurs en mode "Tous" ═══
  // Les users gender='N' peuvent toggle Homme/Femme pour voir le catalogue filtré
  // sans changer leur profil. Default: null (tout voir).
  const [genderFilter, setGenderFilter] = useState(null); // null | 'M' | 'F'

  // Le gender effectif utilisé pour filtrer le catalogue :
  //   - user.gender === 'M' ou 'F' → on respecte ça
  //   - user.gender === 'N' → on utilise genderFilter (M, F, ou null pour tout)
  const effectiveGender = userGender === 'N' ? genderFilter : userGender;

  const genderLabel = userGender === 'M' ? t('gender_man') : userGender === 'N'
    ? (genderFilter === 'M' ? t('gender_man') : genderFilter === 'F' ? t('gender_woman') : t('gender_all'))
    : t('gender_woman');

  const [search,      setSearch]      = useState('');
  // v1.12.255 — Search Agent v2 : filtres extraits via NLP
  const [parsedFilters, setParsedFilters] = useState(null);
  const [parseChips, setParseChips] = useState([]);
  const [autocompleteList, setAutocompleteList] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [typoSuggestion, setTypoSuggestion] = useState(null);
  const searchDebounceRef = useRef(null);

  // v1.12.255 — Debounce 200ms : à chaque saisie, parse + autocomplete
  // v1.12.256 — fix TDZ : on utilise un ref pour allProducts (déclaré plus bas)
  // au lieu de l'inclure dans les deps. Le ref est mis à jour à chaque render via
  // un useEffect séparé après la déclaration de allProducts.
  const allProductsRef = useRef([]);
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!search || search.trim().length < 2) {
      setParsedFilters(null);
      setParseChips([]);
      setAutocompleteList([]);
      setTypoSuggestion(null);
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      try {
        const r = parseSearchQuery({ data: { query: search, user_gender: userGender } });
        if (r?.success && r.data) {
          setParsedFilters(r.data.extracted_filters || null);
          setParseChips(r.data.display_chips || []);
          setTypoSuggestion(r.data.typo_correction?.corrections?.length > 0
            ? r.data.typo_correction
            : null);
        }
        // Autocomplete (lit allProducts via ref pour éviter TDZ)
        const ac = suggestAutocomplete({ partial: search, products: allProductsRef.current || [] });
        setAutocompleteList(ac?.data?.suggestions || []);
      } catch (e) {
        console.warn('[Catalog Search] parse failed:', e?.message);
      }
    }, 200);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [search, userGender]);

  // Retire un chip (réinjecte la requête sans ce mot)
  const removeChip = useCallback((chip) => {
    setParseChips(prev => prev.filter(c => c !== chip));
    // Réécrit la query pour retirer le mot correspondant (best effort)
    const term = String(chip.label || chip.value || '').toLowerCase();
    if (term) {
      setSearch(prev => prev.replace(new RegExp('\\b' + term + '\\b', 'i'), '').replace(/\s+/g, ' ').trim());
    }
  }, []);
  const [category,    setCategory]    = useState(route?.params?.category || 'Tout');
  // v1.12.20 : sous-filtre rapide (Chemises, T-shirts, Sneakers...) sous la catégorie
  const [subFilter,   setSubFilter]   = useState('Tout');

  // ═══ Sécurité : empêcher la catégorie "Robes" pour les hommes ═══
  // Si le user est un homme (via profil ou toggle) et qu'il arrive avec "Robes"
  // (ex: via deeplink ou navigation), on force 'Tout' à la place.
  useEffect(() => {
    if (effectiveGender === 'M' && category === 'Robes') {
      console.log('[CATALOG] ⚠️ Catégorie "Robes" incompatible avec gender=M → retour à Tout');
      setCategory('Tout');
    }
  }, [effectiveGender, category]);

  // v1.12.20 : reset le sous-filtre quand on change de catégorie principale
  useEffect(() => {
    setSubFilter('Tout');
  }, [category]);

  // ═══ FIX 2026-04 : map { productId → ai_metadata } pour trier trending first ═══
  // Rempli en arrière-plan par un useEffect qui appelle enrichCatalogWithAI
  // (batch /catalog-analysis). Permet de savoir quels produits sont trending
  // via ai_metadata.isTrendy et de les mettre en haut de chaque onglet.
  const [aiMetadataById, setAiMetadataById] = useState({});

  // Appliquer le filtre catégorie quand on arrive depuis l'accueil
  // useLayoutEffect pour éviter le flash "Tout" avant le bon filtre
  React.useLayoutEffect(() => {
    const cat = route?.params?.category;
    if (cat) setCategory(cat);
  }, [route?.params?.category, route?.params?.ts]);
  const [partner,     setPartner]     = useState('Tout');
  const [priceRange,  setPriceRange]  = useState(0);
  const [sortBy,      setSortBy]      = useState('relevance');
  const [refreshing,  setRefreshing]  = useState(false);
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // ═══ v1.12.33 : refresh complet ═══
      // 1. Vider le cache AsyncStorage (outfit_dyn_v51_F/M)
      // 2. Incrémenter refreshTrigger → tous les useEffect [userGender, refreshTrigger]
      //    se relancent : iqueens + cashmere via fetchDynamicCatalog,
      //    tous via getCatalog. Comme le cache est vide, fetchDynamicCatalog
      //    retournera les données fraîches du serveur.
      const { invalidateCatalogCache } = await import('../services/DynamicCatalogService');
      await invalidateCatalogCache();
      setRefreshTrigger(t => t + 1);
    } catch (e) {
      console.warn('[CatalogScreen] onRefresh error:', e?.message);
    }
    setTimeout(() => setRefreshing(false), 800);
  }, []);
  const [selected,    setSelected]    = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  // v1.12.353 — dropdown rapide pour le tri (accessible depuis le header)
  const [showSortMenu, setShowSortMenu] = useState(false);
  // v1.12.356 — sidebar desktop repliable pour donner plus d'espace à la grille
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab,   setActiveTab]   = useState('catalog');
  // Filtres avancés
  const [selectedBrand, setSelectedBrand] = useState('Toutes');
  const [selectedColor, setSelectedColor] = useState('Toutes');
  const [onlyFav,       setOnlyFav]       = useState(false);
  const [favIds,        setFavIds]        = useState(new Set());
  // v1.12.345 — nouveau système de filtres avancé (colors multi, brands multi,
  //   sizes, partner_tier, styles, materials, occasions, price_min/max)
  const [advFilters, setAdvFilters] = useState({});
  // Charge les préférences sauvegardées une seule fois au mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prefs = await loadFilterPrefs();
        if (cancelled || !prefs) return;
        const initial = prefsToFilters(prefs);
        if (Object.keys(initial).length > 0) setAdvFilters(initial);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // AWIN live products
  const [boughtProduct,   setBoughtProduct]   = useState(null);
  const [lastOpened,      setLastOpened]      = useState(null); // dernier produit ouvert

  // v1.12.221 — Reset à la position par défaut quand l'utilisateur quitte le Catalogue
  // (et y revient ensuite). Tous les filtres, recherches, scroll → état initial.
  useEffect(() => {
    const { DeviceEventEmitter } = require('react-native');
    const sub = DeviceEventEmitter.addListener('tabs_reset_to_default', () => {
      try { setSearch(''); } catch {}
      try { setCategory('Tout'); } catch {}
      try { setSubFilter('Tout'); } catch {}
      try { setPartner('Tout'); } catch {}
      try { setPriceRange(0); } catch {}
      try { setSortBy('relevance'); } catch {}
      try { setSelectedBrand('Toutes'); } catch {}
      try { setSelectedColor('Toutes'); } catch {}
      try { setOnlyFav(false); } catch {}
      try { setShowFilters(false); } catch {}
      try { setAdvFilters({}); } catch {}
      try { setSelected(null); } catch {}
      try { setActiveTab('catalog'); } catch {}
      try { scrollRef.current?.scrollToOffset?.({ offset: 0, animated: false }); } catch {}
      try { scrollRef.current?.scrollTo?.({ y: 0, animated: false }); } catch {}
    });
    return () => sub.remove();
  }, []);
  const [awinProducts,    setAwinProducts]    = useState([]);
  const [amazonProducts,  setAmazonProducts]  = useState([]);
  const [asosProducts,    setAsosProducts]    = useState([]);
  const [hmProducts,      setHMProducts]      = useState([]);
  const [sovrnProducts,   setSovrnProducts]   = useState([]);
  const [zaraProducts,    setZaraProducts]    = useState([]);
  // v1.12.161 — Boohoo (Massimo Dutti) / COS / Sézane / Reformation : retirés
  // du catalogue (doublons avec Rakuten via Awin / Sovrn auto-tracking).
  const boohooProducts = [], cosProducts = [], sezaneProducts = [], reformationProducts = [];

  // ═══ Tendances mode (IA Claude, cache 6h backend + 1h client) ═══
  const [trends, setTrends] = useState([]);

  const [awinLoading,   setAwinLoading]   = useState(false);
  const [awinPrograms,  setAwinPrograms]  = useState([]);
  const [useAwin,       setUseAwin]       = useState(false);

  // ═══ Scroll ═══
  // scrollRef vient de useResetScrollOnFocus (reset au focus + utilisable pour scrollToTop)
  const flatListRef = scrollRef; // alias pour la rétro-compat
  const [showScrollTop, setShowScrollTop] = useState(false);
  // v1.13.33 — Catalogue initial RAPIDE : un seul /catalog (tous partenaires
  // mélangés) affiché instantanément au montage → plus de "catalogue vide au
  // 1er login/signup". Les chargements détaillés par partenaire enrichissent
  // ensuite (dédup par id).
  const [initialCatalog, setInitialCatalog] = useState([]);

  const scrollToTop = useCallback(() => {
    if (flatListRef.current) {
      flatListRef.current.scrollToOffset({ offset: 0, animated: true });
    }
  }, []);

  // Auto-scroll au changement de catégorie
  useEffect(() => {
    scrollToTop();
  }, [category, scrollToTop]);

  // Charger programmes AWIN une seule fois
  useEffect(() => {
    getApprovedPrograms()
      .then(function(programs) {
        var list = Array.isArray(programs) ? programs : [];
        if (list.length > 0) {
          setAwinPrograms(list);
          setUseAwin(true);
        }
      })
      .catch(function() {});
  }, []); // [] = une seule fois au montage



  // Load Zalando products on mount and when category/gender changes
  // Charger les produits — useRef pour éviter double-appel
  const loadedRef = useRef('');
  const loadKey   = userGender + '|' + category;

  // ═══ Fetch tendances mode IA une fois par gender (cache côté client) ═══
  useEffect(() => {
    let cancelled = false;
    fetchTrendingTags(userGender)
      .then(t => { if (!cancelled) setTrends(t || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userGender]);

  // v1.12.326 — Catalogue restreint à 4 partenaires : iQueens, TOUS, SUIT, ANN
  //   Tous les fetchers fallback (Zalando/Amazon/ASOS/H&M/Zara/Sovrn) désactivés.
  useEffect(() => {
    if (loadedRef.current === loadKey) return;
    loadedRef.current = loadKey;
    setZaraProducts([]);
    setAwinProducts([]);
    setAmazonProducts([]);
    setAsosProducts([]);
    setHMProducts([]);
    setSovrnProducts([]);
  }, [loadKey]);

  // v1.12.161 — Zalando search supprimé (partenaire retiré)


  // Merge AWIN + mock products
  // Appliquer Skimlinks tracking sur tous les produits réels
  const userIdRef = useRef(user?.id || '');
  useEffect(() => { if (user?.id) userIdRef.current = user.id; }, [user?.id]); // eslint-disable-line

  const allProducts = useMemo(() => {
    // Seuls les vrais produits partenaires (plus de mockList statique)
    // ═══ v1.7.81 : ajout tousProducts (depuis partner_products via cron Lambda) ═══
    // v1.12.326 + v1.12.336 — Catalogue restreint à 5 partenaires : iQueens, TOUS, SUIT, ANN, COUTR
    // v1.13.33 — on inclut le catalogue initial rapide EN DERNIER (les loads
    // par partenaire, plus complets, le remplacent via dédup par id).
    const _combined   = [...iqueensProducts, ...tousProducts, ...(suitNegoziProducts||[]), ...(annDemeulemeesterProducts||[]), ...(coutrProducts||[]), ...(drestigeProducts||[]), ...(tamarisProducts||[]), ...(desigualProducts||[]), ...(initialCatalog||[])];
    const _seenIds    = new Set();
    const realRaw     = _combined.filter(p => {
      // v1.13.46 — iQueens SUPPRIMÉ de l'app (sur demande) : filtre défensif à la
      // source (le chargement initial /catalog tous-partenaires en ramène sinon,
      // + purge un éventuel cache local antérieur).
      if (String(p?.partner || '').toLowerCase() === 'iqueens') return false;
      const k = String(p?.id || p?.image_url || p?.image || '');
      if (!k || _seenIds.has(k)) return false;
      _seenIds.add(k); return true;
    });
    // v1.12.163 — boohoo/cos/sezane/reformation retires (vides + chips supprimees)
    const realTracked = applySkimlinksToProducts(realRaw, userIdRef.current);

    // Filtre strict : image valide + lien de redirection + prix > 0
    let filtered = realTracked.filter(p => {
      if (!p) return false;
      const img = p.image || p.image_url;
      if (!img || typeof img !== 'string' || !/^https?:\/\//.test(img)) return false;
      const link = p.affiliateUrl || p.originalUrl || p.url;
      if (!link || typeof link !== 'string' || !/^https?:\/\//.test(link)) return false;
      const priceNum = parseFloat(p.price);
      if (isNaN(priceNum) || priceNum <= 0) return false;
      return true;
    });

    // ═══ RECLASSIFICATION CATÉGORIE via nom de l'article ═══
    // Corrige les erreurs des partners (ex: jupe dans Robes, sac dans Accessoires)
    try {
      const { normalizeCategoriesBatch } = require('../services/CategoryNormalizer');
      const before = filtered.length;
      filtered = normalizeCategoriesBatch(filtered);
      const corrected = filtered.filter(p => p._originalCategory).length;
      if (corrected > 0) console.log(`[CATALOG] catégorie corrigée pour ${corrected} articles (sur ${before})`);
    } catch(e) {}

    // ═══ FILTRE GENRE ═══
    try {
      const GF = require('../services/GenderFilter');
      // v1.12.21 : filterHautsByGenderStrict SUPPRIMÉ (était trop strict).
      // Raison : depuis qu'on a Claude Vision qui analyse 100% du catalogue
      // (ai_metadata.gender), le filtre regex strict sur Hauts rejetait des
      // items unisex légitimes (sweats Carhartt, hoodies oversize, etc.) qui
      // sont parfaitement portables par les femmes. Le filterByGender qui
      // suit garde maintenant les Hauts unisex comme les autres catégories.
      //
      // Le risque (Hauts homme glissés en F) est minimal car :
      //   1. L'IA Claude Vision a déjà classifié correctement la majorité
      //   2. filterByGender reste actif et filtre les genres clairs
      //   3. Le moteur Sélection styliste a ses propres garde-fous

      // ═══ FILTRE GENRE GLOBAL (toutes catégories, garde les unisex) ═══
      if (GF && GF.filterByGender && effectiveGender && ['M', 'F'].includes(effectiveGender)) {
        const before = filtered.length;
        filtered = GF.filterByGender(filtered, effectiveGender);
        if (filtered.length !== before) console.log(`[CATALOG] gender filter (all cats): ${filtered.length}/${before} (target=${effectiveGender})`);
      }
      // ═══ NETTOYAGE CATÉGORIES MAL ATTRIBUÉES ═══
      // Rejette "jupe" dans "Robes", "short" dans "Vestes", etc.
      if (GF && GF.rejectMiscategorized) {
        const before = filtered.length;
        filtered = GF.rejectMiscategorized(filtered);
        if (filtered.length !== before) console.log(`[CATALOG] miscategorized rejected: ${filtered.length}/${before}`);
      }
    } catch(e) {}

    // ═══ DÉDUP + FUSION DES VARIANTES DE TAILLES ═══
    // 1) Premier passage : retirer les vrais doublons par id/sku exacts
    //    (cas où le MÊME flux renvoie 2x la même ligne)
    // 2) Deuxième passage : fusionner les variantes de tailles (cas AWIN/iQueens)
    //    où le même article apparaît 2-3 fois avec des suffixes "XS", "S", "M", "38"
    //    différents mais même brand+nom_normalisé+prix → 1 produit avec sizes agrégées
    // → cohérent avec HomeScreen et TrendingService
    const seenIds  = new Set();
    const seenSkus = new Set();
    const passOne = filtered.filter(function(p) {
      if (p.id && seenIds.has(p.id)) return false;
      const sku = p.zaraId || p.zalandoSku || p.hmCode || p.asosId || p.asin || p.awinId;
      if (sku && seenSkus.has(sku)) return false;
      if (p.id)  seenIds.add(p.id);
      if (sku)   seenSkus.add(sku);
      return true;
    });
    const deduped = mergeDuplicateProducts(passOne, { debug: true });
    if (passOne.length !== deduped.length) {
      console.log(`[CATALOG] fusion variantes : ${filtered.length} → ${passOne.length} (id/sku) → ${deduped.length} (variantes fusionnees)`);
    }

    // ═══ Enrichissement tendances (synchrone, depuis les trends en cache) ═══
    return trends.length > 0 ? enrichProductsWithTrends(deduped, trends) : deduped;
  }, [iqueensProducts, tousProducts, suitNegoziProducts, annDemeulemeesterProducts, coutrProducts, drestigeProducts, tamarisProducts, desigualProducts, initialCatalog, userGender, effectiveGender, trends]);

  // ═══ FIX 2026-04 : charger ai_metadata en arrière-plan pour allProducts ═══
  // Permet à chaque onglet de trier les produits trending (ai_metadata.isTrendy=true)
  // en premier. Le fetch est débouncé via un délai pour attendre que allProducts
  // soit stabilisé (pas de re-fetch à chaque partner qui finit de charger).
  useEffect(() => {
    if (!allProducts || allProducts.length === 0) return;
    const timer = setTimeout(async () => {
      try {
        let token = null;
        try { token = await getValidToken(); } catch {}
        const enriched = await enrichCatalogWithAI(allProducts, token);
        // Construire la map { productId → ai_metadata }
        const byId = {};
        let foundCount = 0;
        for (const p of enriched) {
          if (p.ai_metadata) {
            byId[p.id] = p.ai_metadata;
            foundCount++;
          }
        }
        setAiMetadataById(byId);
        // ═══ Compter aussi les tendances (isTrendy dans ai_metadata OU isTrending via trend tags) ═══
        const trendyCount = Object.values(byId).filter(m => m?.isTrendy === true).length;
        const tagTrendingCount = enriched.filter(p => p.isTrending === true).length;
        console.log(`[CATALOG] ai_metadata loaded: ${foundCount}/${allProducts.length}, trending via ai_metadata.isTrendy=${trendyCount}, trending via tags (isTrending)=${tagTrendingCount}`);
      } catch (e) {
        console.warn('[CATALOG] enrichCatalogWithAI failed:', e.message);
      }
    }, 800); // Debounce 800ms
    return () => clearTimeout(timer);
  }, [allProducts, getValidToken]);

  const priceMax = PRICE_RANGES[priceRange].max;
  const priceMin = PRICE_RANGES[priceRange].min;

  // Extraire listes dynamiques de marques et couleurs selon le contexte
  const availableBrands = useMemo(() => {
    const set = new Set();
    allProducts.forEach(p => {
      if (p.brand && (category === 'Tout' || p.category === category)) set.add(p.brand);
    });
    return ['Toutes', ...Array.from(set).sort()];
  }, [allProducts, category]);

  const availableColors = useMemo(() => {
    const set = new Set();
    allProducts.forEach(p => {
      if (p.color && (category === 'Tout' || p.category === category)) set.add(p.color);
    });
    return ['Toutes', ...Array.from(set).sort()];
  }, [allProducts, category]);

  // Charger les IDs des favoris (pour le filtre "mes favoris uniquement")
  useEffect(() => {
    let mounted = true;
    if (onlyFav) {
      import('../services/FavoritesService').then(mod => {
        mod.getFavorites().then(list => {
          if (mounted) setFavIds(new Set(list.map(f => f.id)));
        });
      });
    }
    return () => { mounted = false; };
  }, [onlyFav]);

  const filtered = useMemo(() => {
    // v1.12.255 — Si parsedFilters contient des critères extraits par le Search Agent,
    // on utilise searchCatalog (filtres structurés multi-critères + tri pertinence).
    // Sinon, fallback sur le match simple name/brand pour compat.
    let list;
    if (search.trim() && parsedFilters && (
      parsedFilters.category || parsedFilters.colors?.length > 0 ||
      parsedFilters.brands?.length > 0 || parsedFilters.materials?.length > 0 ||
      parsedFilters.price_max || parsedFilters.occasions?.length > 0
    )) {
      const searchResult = searchCatalog({
        data: {
          query: search,
          filters: { ...parsedFilters, gender: userGender },
          available_products: allProducts,
          sort_by: 'relevance',
          limit: 1000,
          page: 1,
        },
      });
      list = searchResult?.data?.results || [];
    } else if (search.trim()) {
      // v1.12.268 — Exhaustive search FR+EN multi-champs (synonymes, fuzzy)
      // au lieu du simple includes(name + brand) qui rate la catégorie, AI tags, etc.
      list = exhaustiveSearch(search, allProducts, { gender: userGender, minScore: 20, limit: 1000 });
    } else {
      list = [...allProducts];
    }

    if (category !== 'Tout') {
      // v1.12.20 : Cas spécial "Accessoires + Montres" = inclure les montres
      // qui sont posées dans la catégorie 'Bijoux' (l'IA détecte ~89 montres
      // taggées en Bijoux par les partners). UX : l'user attend les montres
      // sous Accessoires, c'est plus intuitif.
      if (category === 'Accessoires' && subFilter === 'Montres') {
        // On garde les items de Accessoires OU Bijoux qui matchent "Montres"
        list = list.filter(p =>
          (p.category === 'Accessoires' || p.category === 'Bijoux')
          && matchesSubfilter(p, 'Montres')
        );
        console.log(`[CATALOG] sub-filter spécial 'Accessoires + Montres' (incl. Bijoux): ${list.length}`);
      } else {
        // ═══ Filtre catégorie strict : valide via le nom pour éviter les articles mal catégorisés
        // (ex: jupe dans "Robes", short dans "Vestes" côté partenaire)
        try {
          const { filterMiscategorized } = require('../services/GenderFilter');
          list = filterMiscategorized(list, category);
        } catch (e) {
          // Fallback si le helper n'est pas dispo
          list = list.filter(p => p.category === category);
        }

        // v1.12.20 : Sous-filtre rapide (Chemises, T-shirts, Sneakers...)
        if (subFilter && subFilter !== 'Tout') {
          const before = list.length;
          list = list.filter(p => matchesSubfilter(p, subFilter));
          console.log(`[CATALOG] sub-filter '${subFilter}': ${list.length}/${before}`);
        }
      }
    }
    if (partner       !== 'Tout')    list = list.filter(p => p.partner  === partner);
    if (selectedBrand !== 'Toutes')  list = list.filter(p => p.brand === selectedBrand);
    if (selectedColor !== 'Toutes')  list = list.filter(p => p.color === selectedColor);
    if (onlyFav && favIds.size > 0)  list = list.filter(p => favIds.has(p.id));
    if (priceMax)                    list = list.filter(p => p.price <= priceMax);
    if (priceMin)                    list = list.filter(p => p.price >= priceMin);

    // v1.12.345 — nouveau filtre avancé (colors multi, brands multi, tier, styles, etc.)
    if (advFilters && Object.keys(advFilters).length > 0) {
      list = applyAdvFilters(list, advFilters);
    }

    // ═══ FIX 2026-04 : enrichir chaque produit avec son ai_metadata depuis la map ═══
    // Ensuite on peut utiliser isProductTrending() qui lit ai_metadata.isTrendy
    const enriched = list.map(p => {
      const aiMeta = aiMetadataById[p.id];
      if (aiMeta) return { ...p, ai_metadata: aiMeta };
      return p;
    });

    // ═══ FIX 2026-04 : TRENDING FIRST dans chaque onglet ═══
    // Règle : dans TOUS les cas (sauf quand l'user choisit un tri explicite par
    // prix/rating/discount), les produits trending apparaissent en premier,
    // triés par trendScore décroissant. Pour les tris explicites (prix, etc.),
    // on applique le tri demandé mais trending-first reste actif comme clé
    // secondaire → les produits trending sont en tête du groupe de prix similaire.
    //
    // v1.12.893 — Score compatibilité saison+température courante (0=mismatch, 1=neutre, 2=match partiel, 3=match complet).
    // Logique : item dont seasonality/temperatureRange existe et matche → priorisé.
    // Item sans info → neutre. Item avec info qui mismatch → pénalisé.
    const seasonScore = (p) => {
      const ai = p.ai_metadata;
      if (!ai) return 1;
      const s = _seasonMatch(ai, currentSeason);
      const t = _tempMatch(ai, currentTempC);
      if (s === null && t === null) return 1; // pas d'info → neutre
      const sBonus = s === true ? 1 : s === false ? -1 : 0;
      const tBonus = t === true ? 1 : t === false ? -1 : 0;
      return 1 + sBonus + tBonus; // range : -1 à 3
    };

    // Double sort : on peut faire un stable sort en combinant les critères.
    const withTrendFlag = enriched.map(p => ({
      p,
      isTrending: isProductTrending(p),
      trendScore: (p.ai_metadata?.trendScore) || (p.trendScore) || 0,
      seasonScore: seasonScore(p),
    }));

    if (sortBy === 'price_asc') {
      withTrendFlag.sort((a, b) => {
        if (a.isTrending !== b.isTrending) return a.isTrending ? -1 : 1;
        if (a.seasonScore !== b.seasonScore) return b.seasonScore - a.seasonScore;
        return a.p.price - b.p.price;
      });
    } else if (sortBy === 'price_desc') {
      withTrendFlag.sort((a, b) => {
        if (a.isTrending !== b.isTrending) return a.isTrending ? -1 : 1;
        if (a.seasonScore !== b.seasonScore) return b.seasonScore - a.seasonScore;
        return b.p.price - a.p.price;
      });
    } else if (sortBy === 'rating') {
      withTrendFlag.sort((a, b) => {
        if (a.isTrending !== b.isTrending) return a.isTrending ? -1 : 1;
        if (a.seasonScore !== b.seasonScore) return b.seasonScore - a.seasonScore;
        return (b.p.rating || 0) - (a.p.rating || 0);
      });
    } else if (sortBy === 'discount') {
      withTrendFlag.sort((a, b) => {
        if (a.isTrending !== b.isTrending) return a.isTrending ? -1 : 1;
        if (a.seasonScore !== b.seasonScore) return b.seasonScore - a.seasonScore;
        return (b.p.discount || 0) - (a.p.discount || 0);
      });
    } else {
      // 'relevance' (défaut) : trending d'abord → saisonnalité → trendScore desc
      withTrendFlag.sort((a, b) => {
        if (a.isTrending !== b.isTrending) return a.isTrending ? -1 : 1;
        if (a.seasonScore !== b.seasonScore) return b.seasonScore - a.seasonScore;
        if (a.isTrending && b.isTrending) return b.trendScore - a.trendScore;
        return 0;
      });
    }

    return withTrendFlag.map(x => x.p);
  }, [allProducts, search, parsedFilters, userGender, category, subFilter, partner, selectedBrand, selectedColor, onlyFav, favIds, priceRange, sortBy, aiMetadataById, advFilters, currentSeason, currentTempC]);

  // v1.13.43 — Masquage runtime des tuiles à image morte (404 confirmé par
  // SmartImage après tous les proxies). Dès qu'une image est marquée morte, la
  // version change → on retire l'article du catalogue (« pas d'image = pas
  // affiché »). Le même registre exclut l'article de la sélection styliste.
  const deadImagesVersion = useDeadImagesVersion();
  const visibleData = useMemo(
    () => (filtered || []).filter(p => !isItemImageDead(p)),
    [filtered, deadImagesVersion]
  );

  // v1.12.345 — base pour compteurs du panneau de filtres (sans advFilters appliqué)
  //   → les compteurs reflètent le contexte courant (genre + partenaire + catégorie + sous-filtre)
  //   mais ne se réduisent pas quand l'user choisit une couleur dans le panneau.
  const baseForFilters = useMemo(() => {
    let pool = allProducts || [];
    if (category !== 'Tout') {
      try {
        const { filterMiscategorized } = require('../services/GenderFilter');
        pool = filterMiscategorized(pool, category);
      } catch (e) {
        pool = pool.filter(p => p.category === category);
      }
      if (subFilter && subFilter !== 'Tout') {
        pool = pool.filter(p => matchesSubfilter(p, subFilter));
      }
    }
    if (partner !== 'Tout') pool = pool.filter(p => p.partner === partner);
    return pool;
  }, [allProducts, category, subFilter, partner]);

  // v1.12.354 — passe advFilters comme currentFilters pour des compteurs dynamiques
  //   (chaque dimension exclut son propre filtre du pool de comptage)
  const availableFiltersData = useMemo(
    () => computeAvailableFilters(baseForFilters, { currentFilters: advFilters }),
    [baseForFilters, advFilters]
  );

  // Masque la catégorie "Robes" + partenaire "TOUS" pour les hommes
  // (cohérent avec CATS_M et la barre chips ligne 1903 — v1.12.295)
  const availableFiltersForPanel = useMemo(() => {
    if (effectiveGender !== 'M') return availableFiltersData;
    return {
      ...availableFiltersData,
      categories: (availableFiltersData.categories || []).filter(c => c.value !== 'Robes'),
      partners:   (availableFiltersData.partners   || []).filter(p => p.value !== 'tous'),
    };
  }, [availableFiltersData, effectiveGender]);

  // Nettoie advFilters.category si "Robes" y est et que l'user est un homme
  useEffect(() => {
    if (effectiveGender !== 'M') return;
    if (!Array.isArray(advFilters.category) || !advFilters.category.includes('Robes')) return;
    const next = { ...advFilters };
    next.category = advFilters.category.filter(c => c !== 'Robes');
    if (next.category.length === 0) delete next.category;
    setAdvFilters(next);
  }, [effectiveGender, advFilters]);

  // v1.12.374 — Nettoie advFilters.partners si "tous" y est et que l'user est un homme
  useEffect(() => {
    if (effectiveGender !== 'M') return;
    if (!Array.isArray(advFilters.partners) || !advFilters.partners.includes('tous')) return;
    const next = { ...advFilters };
    next.partners = advFilters.partners.filter(p => p !== 'tous');
    if (next.partners.length === 0) delete next.partners;
    setAdvFilters(next);
  }, [effectiveGender, advFilters]);

  // v1.12.374 — Si l'user homme a sélectionné 'tous' comme partenaire actif
  // (state legacy `partner`), reset à 'Tout'
  useEffect(() => {
    if (effectiveGender === 'M' && partner === 'tous') {
      setPartner('Tout');
    }
  }, [effectiveGender, partner]);

  // Chips actifs du nouveau panneau (filtres avancés) — bilingue FR/EN
  const advActiveChips = useMemo(() => buildActiveChips(advFilters, lang), [advFilters, lang]);
  const advActiveCount = useMemo(() => countActiveFilters(advFilters), [advFilters]);

  // v1.12.256 — Sync ref pour le Search Agent autocomplete (déclaré plus haut, TDZ-safe)
  React.useEffect(() => {
    allProductsRef.current = allProducts || [];
  }, [allProducts]);

  // ═══ Prefetch des 20 premières images du catalogue filtré ═══
  // Accélère significativement le scroll — images déjà en cache mémoire RN
  React.useEffect(() => {
    if (!filtered || filtered.length === 0) return;
    try {
      const { prefetchImages } = require('../services/ImageCacheService');
      const uris = filtered.slice(0, 20).map(p => p.image).filter(Boolean);
      if (uris.length > 0) {
        prefetchImages(uris, { priority: 'high' });
      }
    } catch {}
  }, [filtered]);

  // ═══ Compteurs par catégorie ═══
  // FIX 2026-04 : utilise `allProducts` comme source unique (cohérent avec
  // `filtered` qui est aussi basé sur `allProducts`). Avant, `catCounts` partait
  // d'un pool indépendant (Zalando+Asos+HM+Zara avec category='Tout') qui
  // n'incluait ni Awin ni Amazon → compteurs incohérents avec le header.
  // Applique les mêmes filtres que `filtered` SAUF la catégorie.
  const catCounts = useMemo(() => {
    let pool = allProducts;

    if (search) {
      pool = pool.filter(p =>
        (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.brand || '').toLowerCase().includes(search.toLowerCase())
      );
    }
    if (partner       !== 'Tout')    pool = pool.filter(p => p.partner  === partner);
    if (selectedBrand !== 'Toutes')  pool = pool.filter(p => p.brand === selectedBrand);
    if (selectedColor !== 'Toutes')  pool = pool.filter(p => p.color === selectedColor);
    if (onlyFav && favIds.size > 0)  pool = pool.filter(p => favIds.has(p.id));
    if (priceMax)                    pool = pool.filter(p => p.price <= priceMax);
    if (priceMin)                    pool = pool.filter(p => p.price >= priceMin);

    // Compter par catégorie en utilisant la même reclassification que `filtered`
    // pour Tout (pool.length) mais aussi par catégorie avec filterMiscategorized.
    const counts = { Tout: pool.length };
    let filterMiscat = null;
    try {
      filterMiscat = require('../services/GenderFilter').filterMiscategorized;
    } catch (e) {}

    // Préagréger par catégorie native
    const byCat = {};
    for (const p of pool) {
      if (!p.category) continue;
      if (!byCat[p.category]) byCat[p.category] = [];
      byCat[p.category].push(p);
    }
    // Pour chaque catégorie, appliquer le même filtre que `filtered` quand
    // l'onglet est sélectionné (filterMiscategorized) → compteur strictement
    // égal à ce qui s'affichera quand l'user clique sur l'onglet.
    for (const cat of Object.keys(byCat)) {
      const arr = byCat[cat];
      if (filterMiscat) {
        try { counts[cat] = filterMiscat(arr, cat).length; }
        catch (e) { counts[cat] = arr.length; }
      } else {
        counts[cat] = arr.length;
      }
    }
    return counts;
  }, [allProducts, search, partner, selectedBrand, selectedColor, onlyFav, favIds, priceRange]);

  // ═══ v1.12.23 : Compteurs par sous-filtre (pour la 2ème barre d'onglets) ═══
  // Calcule combien d'items chaque sous-filtre va afficher, après application
  // de tous les autres filtres (catégorie, prix, marque, etc.) MAIS avant le
  // sous-filtre lui-même.
  // → l'user voit "Tout 343 · Courtes 19 · Mi-longues 11 · Sans manches 39 ..."
  const subCounts = useMemo(() => {
    // Si pas de catégorie principale ou pas de sous-filtres dispo → rien à compter
    const subList = getSubfiltersFor(category, effectiveGender || 'N');
    if (!subList || subList.length === 0) return {};

    // Construire le pool en appliquant TOUS les filtres SAUF le sous-filtre
    let pool = allProducts;
    if (search) {
      pool = pool.filter(p =>
        (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.brand || '').toLowerCase().includes(search.toLowerCase())
      );
    }
    if (category !== 'Tout') {
      // Cas spécial Accessoires + Montres : on inclut Bijoux
      if (category === 'Accessoires') {
        // Pour le compteur "Montres", on inclut Bijoux ; pour les autres
        // sous-filtres d'Accessoires, on garde Accessoires seuls.
        // → on applique le filtre catégorie strict ici, et on traitera Montres
        //   spécifiquement dans la boucle ci-dessous.
        try {
          const { filterMiscategorized } = require('../services/GenderFilter');
          pool = filterMiscategorized(pool, category);
        } catch (e) {
          pool = pool.filter(p => p.category === category);
        }
      } else {
        try {
          const { filterMiscategorized } = require('../services/GenderFilter');
          pool = filterMiscategorized(pool, category);
        } catch (e) {
          pool = pool.filter(p => p.category === category);
        }
      }
    }
    if (partner       !== 'Tout')    pool = pool.filter(p => p.partner  === partner);
    if (selectedBrand !== 'Toutes')  pool = pool.filter(p => p.brand === selectedBrand);
    if (selectedColor !== 'Toutes')  pool = pool.filter(p => p.color === selectedColor);
    if (onlyFav && favIds.size > 0)  pool = pool.filter(p => favIds.has(p.id));
    if (priceMax)                    pool = pool.filter(p => p.price <= priceMax);
    if (priceMin)                    pool = pool.filter(p => p.price >= priceMin);

    // Pool spécial pour "Montres" sous Accessoires : on prend Accessoires + Bijoux
    let montresPool = null;
    if (category === 'Accessoires') {
      montresPool = allProducts.filter(p =>
        p.category === 'Accessoires' || p.category === 'Bijoux'
      );
      if (search) {
        montresPool = montresPool.filter(p =>
          (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
          (p.brand || '').toLowerCase().includes(search.toLowerCase())
        );
      }
      if (partner       !== 'Tout')    montresPool = montresPool.filter(p => p.partner  === partner);
      if (selectedBrand !== 'Toutes')  montresPool = montresPool.filter(p => p.brand === selectedBrand);
      if (selectedColor !== 'Toutes')  montresPool = montresPool.filter(p => p.color === selectedColor);
      if (onlyFav && favIds.size > 0)  montresPool = montresPool.filter(p => favIds.has(p.id));
      if (priceMax)                    montresPool = montresPool.filter(p => p.price <= priceMax);
      if (priceMin)                    montresPool = montresPool.filter(p => p.price >= priceMin);
    }

    // Compter pour chaque sous-filtre
    const counts = { Tout: pool.length };
    for (const sub of subList) {
      if (sub === 'Tout') continue;
      // Cas spécial Montres dans Accessoires
      if (category === 'Accessoires' && sub === 'Montres' && montresPool) {
        counts[sub] = montresPool.filter(p => matchesSubfilter(p, 'Montres')).length;
      } else {
        counts[sub] = pool.filter(p => matchesSubfilter(p, sub)).length;
      }
    }
    return counts;
  }, [allProducts, search, category, effectiveGender, partner, selectedBrand, selectedColor, onlyFav, favIds, priceRange]);

  const renderProductItem = React.useCallback(({ item }) => (
    <CatalogProductCard
      product={item}
      onPress={() => { setSelected(item); setLastOpened(item); }}
    />
  ), []);

  return (
    <>
    <SafeAreaView style={[s.safe, { backgroundColor: theme.bg }]}>

      {/* ═════ Header premium ═════ */}
      <View style={[s.header, { backgroundColor: theme.bg, borderBottomColor: theme.bgBorder }]}>
        <View style={{ flex: 1 }}>
          <Text style={[s.catalogBadge, { color: '#B8860B' }]}>{t('boutique_badge')}</Text>
          <Text style={[s.title, { color: theme.text }]}>{t('nav_catalog')}</Text>
          <View style={s.subtitleRow}>
            <Text style={s.subtitle}>{visibleData.length} {t('articles_count')}</Text>
            {/* v1.12.359 — badge AWIN supprimé à la demande user (artefact technique non-utile UX) */}
          </View>
        </View>
        <View style={s.headerRight}>
          {search.length > 0 && (
            <TouchableOpacity onPress={() => { setSearch(''); setAwinProducts([]); }}>
              <Text style={s.clearBtn}>✕</Text>
            </TouchableOpacity>
          )}
          {/* v1.12.353 — bouton Tri rapide visible partout */}
          <TouchableOpacity
            style={[s.filterBtn, showSortMenu && s.filterBtnActive, { marginRight: 6 }]}
            onPress={() => setShowSortMenu(v => !v)}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={(lang || '').startsWith('fr') ? 'Trier les produits' : 'Sort products'}
          >
            <Text style={[s.filterBtnIcon, showSortMenu && s.filterBtnIconActive]}>↕</Text>
            <Text style={[s.filterBtnLabel, showSortMenu && s.filterBtnLabelActive]}>
              {(() => {
                const isFR = (lang || '').startsWith('fr');
                const labels = {
                  relevance:  isFR ? 'PERTINENCE' : 'RELEVANCE',
                  price_asc:  isFR ? 'PRIX ↑'     : 'PRICE ↑',
                  price_desc: isFR ? 'PRIX ↓'     : 'PRICE ↓',
                  discount:   isFR ? 'PROMO'      : 'DISCOUNT',
                  rating:     isFR ? 'NOTE'       : 'RATING',
                };
                return labels[sortBy] || labels.relevance;
              })()}
            </Text>
          </TouchableOpacity>
          {/* Bouton FILTRES : visible sur mobile, ou sur desktop quand sidebar repliée */}
          {(!isDesktopWeb || sidebarCollapsed) && (
          <TouchableOpacity
            style={[s.filterBtn, (showFilters || (isDesktopWeb && !sidebarCollapsed)) && s.filterBtnActive]}
            onPress={() => {
              if (isDesktopWeb) {
                setSidebarCollapsed(false);
              } else {
                setShowFilters(!showFilters);
              }
            }}
            activeOpacity={0.7}>
            <Text style={[s.filterBtnIcon, showFilters && s.filterBtnIconActive]}>⇅</Text>
            <Text style={[s.filterBtnLabel, showFilters && s.filterBtnLabelActive]}>{t('filters_caps')}</Text>
            {(() => {
              let count = 0;
              if (priceRange > 0) count++;
              if (selectedBrand !== 'Toutes') count++;
              if (selectedColor !== 'Toutes') count++;
              if (sortBy !== 'relevance') count++;
              if (onlyFav) count++;
              if (partner !== 'Tout') count++;
              // v1.12.345 — inclure les nouveaux filtres avancés
              count += advActiveCount;
              return count > 0 ? (
                <View style={s.filterBadge}>
                  <Text style={s.filterBadgeText}>{count}</Text>
                </View>
              ) : null;
            })()}
          </TouchableOpacity>
          )}
        </View>
      </View>

      {/* v1.12.353 — Menu déroulant Tri (positionné sous le header, fermable backdrop) */}
      {showSortMenu && (() => {
        const isFR = (lang || '').startsWith('fr');
        const opts = [
          { key: 'relevance',  fr: 'Pertinence',  en: 'Relevance' },
          { key: 'price_asc',  fr: 'Prix croissant',   en: 'Price low to high' },
          { key: 'price_desc', fr: 'Prix décroissant', en: 'Price high to low' },
          { key: 'discount',   fr: 'Meilleures promos', en: 'Best deals' },
          { key: 'rating',     fr: 'Mieux notés',       en: 'Top rated' },
        ];
        return (
          <>
            {/* backdrop full-page pour fermer au clic extérieur */}
            <TouchableOpacity
              activeOpacity={1}
              onPress={() => setShowSortMenu(false)}
              style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                zIndex: 50,
              }}
            />
            <View style={{
              position: 'absolute',
              top: 70, right: 14,
              backgroundColor: '#FFFFFF',
              borderRadius: 12,
              paddingVertical: 6,
              minWidth: 220,
              zIndex: 51,
              borderWidth: 1,
              borderColor: 'rgba(0,0,0,0.08)',
              ...(Platform.OS === 'web' ? { boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } : {
                shadowColor: '#000', shadowOpacity: 0.15, shadowRadius: 12, shadowOffset: { width: 0, height: 4 }, elevation: 5,
              }),
            }}>
              {opts.map(opt => {
                const active = sortBy === opt.key;
                return (
                  <TouchableOpacity
                    key={opt.key}
                    onPress={() => { setSortBy(opt.key); setShowSortMenu(false); }}
                    style={{
                      paddingHorizontal: 16,
                      paddingVertical: 12,
                      minHeight: 48,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: active ? '#F5F4F0' : 'transparent',
                    }}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                  >
                    <Text style={{
                      fontSize: 14,
                      color: active ? '#9B7B5A' : '#1F2937',
                      fontWeight: active ? '700' : '500',
                    }}>
                      {isFR ? opt.fr : opt.en}
                    </Text>
                    {active ? <Text style={{ color: '#9B7B5A', fontSize: 14, fontWeight: '700' }}>✓</Text> : null}
                  </TouchableOpacity>
                );
              })}
            </View>
          </>
        );
      })()}

      {/* v1.12.345 — wrapper responsive : row sur desktop (sidebar + grille), column sur mobile */}
      <View style={{ flex: 1, flexDirection: isDesktopWeb ? 'row' : 'column' }}>
        {/* Sidebar persistante sur desktop ≥ 768 (à gauche). Repliable via "Voir les X produits". */}
        {isDesktopWeb && !sidebarCollapsed && (
          <CatalogFiltersPanel
            mode="sidebar"
            filters={advFilters}
            available={availableFiltersForPanel}
            onChange={(nextFilters) => {
              setAdvFilters(nextFilters);
              // v1.12.350 — sync legacy `category`/`subFilter` pour que la section Sous-catégorie
              //   du panel utilise la même logique matchesSubfilter que les chips mobiles.
              const nextCats = Array.isArray(nextFilters.category) ? nextFilters.category : [];
              if (nextCats.length === 1) {
                setCategory(nextCats[0]);
              } else {
                setCategory('Tout');
                setSubFilter('Tout');
              }
              try {
                saveFilterPrefs({
                  favorite_brands:  nextFilters.brands  || [],
                  favorite_styles:  nextFilters.styles  || [],
                  preferred_colors: nextFilters.colors  || [],
                  price_range_preferred:
                    (typeof nextFilters.price_min === 'number' || typeof nextFilters.price_max === 'number')
                      ? [nextFilters.price_min || 0, nextFilters.price_max || null]
                      : null,
                });
              } catch {}
            }}
            onReset={() => { setAdvFilters({}); setCategory('Tout'); setSubFilter('Tout'); }}
            onApply={() => setSidebarCollapsed(true)}
            resultsCount={filtered.length}
            locale={lang}
            subfilterList={category !== 'Tout' ? ['Tout', ...(getSubfiltersFor(category, effectiveGender || 'N') || []).filter(s => s !== 'Tout')] : null}
            subfilterCounts={subCounts}
            currentSubfilter={subFilter}
            onSubfilterChange={(sub) => setSubFilter(sub)}
            labelForSubfilter={labelForSub}
            currentSort={sortBy}
            onSortChange={(k) => setSortBy(k)}
          />
        )}

        {/* Colonne principale : chips + recherche + grille */}
        <View style={{ flex: 1 }}>

      {/* ═════ Toggle Homme/Femme (visible uniquement si user en mode "Tous") ═════ */}
      {userGender === 'N' && (
        <View style={{
          flexDirection: 'row',
          paddingHorizontal: 16,
          paddingVertical: 10,
          gap: 8,
          borderBottomWidth: 1,
          borderBottomColor: '#EEE',
        }}>
          {[
            { key: null, label: t('gender_all'),   emoji: '◎' },
            { key: 'F',  label: t('gender_woman'), emoji: '♀' },
            { key: 'M',  label: t('gender_man'),   emoji: '♂' },
          ].map(opt => {
            const active = genderFilter === opt.key;
            return (
              <TouchableOpacity
                key={opt.key || 'all'}
                onPress={() => setGenderFilter(opt.key)}
                activeOpacity={0.85}
                style={{
                  flex: 1,
                  paddingVertical: 10,
                  paddingHorizontal: 12,
                  borderRadius: 20,
                  backgroundColor: active ? '#1A1A1A' : '#F5F5F5',
                  borderWidth: 1,
                  borderColor: active ? '#1A1A1A' : '#E0E0E0',
                  alignItems: 'center',
                }}
              >
                <Text style={{
                  fontSize: 13,
                  fontWeight: active ? '700' : '500',
                  color: active ? '#D4AF72' : '#666',
                  letterSpacing: 0.5,
                }}>
                  {opt.emoji}  {opt.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      {/* ═════ Filtre partenaires (chip par marchand) ═════
            v1.12.348 — masqué sur desktop : la sidebar a une section Partenaires.
            v1.12.358 — masqué aussi sur mobile : redondant avec le modal panel
            qui a aussi la section Partenaires. */}
      {false && (
      <View style={s.partnerScrollWrap}>
        <ChipsRow contentContainerStyle={s.partnerRow}>
          <TouchableOpacity
            style={[s.partnerChip, partner === 'Tout' && s.partnerChipActive]}
            onPress={() => setPartner('Tout')}
            activeOpacity={0.85}>
            <Text style={[s.partnerLabel, partner === 'Tout' && s.partnerLabelActive]}>{t('all_partners')}</Text>
          </TouchableOpacity>
          {[
            // v1.12.322 — Ordre : SUIT + COUTR + ANN en première position (priorité Mytenue luxe)
            { key: 'suit_negozi',        label: 'SUIT' },
            // v1.12.336 — COUTR (USA luxe, prix USD)
            { key: 'coutr',              label: 'COUTR' },
            { key: 'ann_demeulemeester', label: 'ANN' },
            // v1.12.295 — TOUS masque pour les hommes (0 articles M chez TOUS)
            ...(effectiveGender === 'M' ? [] : [{ key: 'tous', label: 'TOUS' }]),
            // v1.12.366 — iQueens supprimé de l'app (sur demande user)
          ].map(p => (
            <TouchableOpacity key={p.key}
              style={[s.partnerChip, partner === p.key && s.partnerChipActive]}
              onPress={() => setPartner(partner === p.key ? 'Tout' : p.key)}
              activeOpacity={0.85}>
              <PartnerLogo partner={p.key} size={16} />
              <Text style={[s.partnerLabel, partner === p.key && s.partnerLabelActive]}>{p.label}</Text>
            </TouchableOpacity>
          ))}
        </ChipsRow>
      </View>
      )}

      {/* v1.12.92 — Bannière de diagnostic TOUS retirée sur demande user */}

      {/* ═════ Category pills — premium avec compteurs ═════
              v1.12.348 — masqué sur desktop (sidebar a la section Catégorie).
              v1.12.358 — masqué aussi sur mobile : redondant avec le modal panel. */}
      {false && (
      <View style={s.catScrollWrap}>
        <ChipsRow contentContainerStyle={s.catRow}>
          {getCategoriesByGender(effectiveGender || 'N')
            // v1.12.302 — masquer chips vides (sauf "Tout" qui reste toujours)
            .filter(cat => cat === 'Tout' || (catCounts[cat] || 0) > 0)
            .map(cat => {
            const count = catCounts[cat] || 0;
            return (
              <TouchableOpacity key={cat}
                style={[s.catChipPremium, category === cat && s.catChipPremiumActive]}
                onPress={() => {
                  setCategory(cat);
                  // ═══ Scroll en haut au changement d'onglet catégorie ═══
                  try { flatListRef.current?.scrollToOffset?.({ offset: 0, animated: false }); } catch {}
                }}
                activeOpacity={0.85}>
                <Text style={[s.catLabelPremium, category === cat && s.catLabelPremiumActive]}>{labelForCat(cat)}</Text>
                <View style={[s.catCountBubblePrem, category === cat && s.catCountBubblePremActive, count === 0 && s.catCountBubbleEmpty]}>
                  <Text style={[s.catCountTextPrem, category === cat && s.catCountTextPremActive, count === 0 && s.catCountTextEmpty]}>{count}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </ChipsRow>
      </View>
      )}

      {/* ═════ v1.12.20 : Sous-filtres rapides (Chemises, T-shirts, Sneakers...) ═════
              v1.12.348 — masqué sur desktop (sidebar a la section Type dynamique).
              v1.12.358 — masqué aussi sur mobile : redondant avec le modal panel. */}
      {false && (() => {
        const subList = getSubfiltersFor(category, effectiveGender || 'N');
        if (!subList || subList.length === 0) return null;
        return (
          <View style={s.subfilterScrollWrap}>
            <ChipsRow contentContainerStyle={s.subfilterRow}>
              {subList.map(sub => {
                // v1.12.23 : compteur par sous-filtre
                const count = subCounts[sub] != null ? subCounts[sub] : null;
                const isActive = subFilter === sub;
                const isEmpty = count === 0;
                return (
                  <TouchableOpacity
                    key={sub}
                    style={[s.subfilterChip, isActive && s.subfilterChipActive]}
                    onPress={() => {
                      setSubFilter(sub);
                      try { flatListRef.current?.scrollToOffset?.({ offset: 0, animated: false }); } catch {}
                    }}
                    activeOpacity={0.85}>
                    <Text style={[
                      s.subfilterLabel,
                      isActive && s.subfilterLabelActive,
                      isEmpty && !isActive && s.subfilterLabelEmpty,
                    ]}>
                      {labelForSub(sub)}
                      {count != null && (
                        <Text style={[
                          s.subfilterCount,
                          isActive && s.subfilterCountActive,
                          isEmpty && !isActive && s.subfilterCountEmpty,
                        ]}>
                          {' '}{count}
                        </Text>
                      )}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ChipsRow>
          </View>
        );
      })()}

      {/* v1.12.255+257 — Search Agent v2 : barre de recherche INTELLIGENTE
          v1.12.262 — Chips intégrés DANS la barre (à droite) pour 0 espace vertical en plus */}
      <View style={[searchStyles.bar, { backgroundColor: theme.bgSurface || '#FFFFFF', borderColor: theme.bgBorder }]}>
        <Text style={searchStyles.icon}>🔍</Text>
        <TextInput
          value={search}
          onChangeText={(v) => { setSearch(v); setShowAutocomplete(true); }}
          onFocus={() => setShowAutocomplete(true)}
          onBlur={() => setTimeout(() => setShowAutocomplete(false), 150)}
          placeholder={t('search_catalog_placeholder')}
          placeholderTextColor={theme.textFaint || '#A89E94'}
          style={[searchStyles.input, { color: theme.text, flex: parseChips.length > 0 ? 0.5 : 1 }]}
          returnKeyType="search"
        />
        {parseChips.length > 0 && (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={searchStyles.chipsInline}
            keyboardShouldPersistTaps="handled">
            {parseChips.slice(0, 6).map((chip, i) => {
              const lbl = String(chip.label || '');
              const short = lbl.length > 14 ? lbl.slice(0, 12) + '…' : lbl;
              return (
                <TouchableOpacity
                  key={i}
                  style={searchStyles.chip}
                  activeOpacity={0.85}
                  onPress={() => removeChip(chip)}>
                  <Text style={searchStyles.chipText} numberOfLines={1}>{short}</Text>
                  <Text style={searchStyles.chipX}>✕</Text>
                </TouchableOpacity>
              );
            })}
            {parseChips.length > 6 && (
              <View style={searchStyles.chipMore}>
                <Text style={searchStyles.chipMoreText}>+{parseChips.length - 6}</Text>
              </View>
            )}
          </ScrollView>
        )}
        {search.length > 0 && (
          <TouchableOpacity onPress={() => { setSearch(''); setParsedFilters(null); setParseChips([]); }} hitSlop={{top:8,right:8,bottom:8,left:8}}>
            <Text style={searchStyles.clear}>✕</Text>
          </TouchableOpacity>
        )}
      </View>

      {typoSuggestion && typoSuggestion.display_message && (
        <TouchableOpacity
          style={searchStyles.typoBanner}
          activeOpacity={0.85}
          onPress={() => setSearch(typoSuggestion.corrected || typoSuggestion.original)}>
          <Text style={searchStyles.typoText}>{typoSuggestion.display_message}</Text>
        </TouchableOpacity>
      )}

      {/* v1.12.258 — Autocomplete : caché si chips déjà extraits (user a fini de taper)
          → évite de pousser les articles vers le bas */}
      {showAutocomplete && autocompleteList.length > 0 && parseChips.length === 0 && (
        <View style={searchStyles.autoBox}>
          {autocompleteList.slice(0, 4).map((sug, i) => (
            <TouchableOpacity
              key={i}
              style={searchStyles.autoItem}
              activeOpacity={0.7}
              onPress={() => { setSearch(sug.text); setShowAutocomplete(false); }}>
              <Text style={{ fontSize: 13, marginRight: 6 }}>{sug.icon || '🔎'}</Text>
              <Text style={searchStyles.autoText}>{sug.text}</Text>
              <Text style={searchStyles.autoType}>{sug.type === 'brand' ? t('ac_brand') : sug.type === 'color' ? t('ac_color') : sug.type === 'category' ? t('ac_category') : ''}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/* Trending : SEULEMENT si barre vide ET focus (pas si l'user a tapé qqch) */}
      {showAutocomplete && !search.trim() && parseChips.length === 0 && (
        <View style={searchStyles.trendingBox}>
          <Text style={searchStyles.trendingTitle}>🔥 {t('trending_this_week')}</Text>
          {(getTrendingSearches().data?.trending || []).slice(0, 4).map(t => (
            <TouchableOpacity
              key={t.rank}
              style={searchStyles.autoItem}
              activeOpacity={0.7}
              onPress={() => { setSearch(t.query); setShowAutocomplete(false); }}>
              <Text style={{ fontSize: 13, marginRight: 6 }}>{t.icon}</Text>
              <Text style={searchStyles.autoText}>{t.query}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/* v1.12.345 — Barre de chips actifs (filtres avancés) avec ✕ removable */}
      {advActiveChips.length > 0 && (
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: 12,
          paddingVertical: 8,
          borderBottomWidth: 1,
          borderBottomColor: '#EEE',
          backgroundColor: theme.bg,
        }}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ gap: 6, paddingRight: 8 }}
            style={{ flex: 1 }}
          >
            {advActiveChips.map((chip, i) => (
              <TouchableOpacity
                key={`${chip.filter_id}-${chip.value}-${i}`}
                onPress={() => setAdvFilters(prev => removeAdvFilter(prev, chip.filter_id, chip.value))}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingLeft: 10,
                  paddingRight: 6,
                  paddingVertical: 5,
                  borderRadius: 14,
                  backgroundColor: '#1F2937',
                  gap: 4,
                }}
                activeOpacity={0.7}
              >
                <Text style={{ color: '#FFF', fontSize: 12, fontWeight: '500' }} numberOfLines={1}>
                  {chip.label}
                </Text>
                <Text style={{ color: '#FFF', fontSize: 13, fontWeight: '700', paddingHorizontal: 3 }}>×</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <TouchableOpacity
            onPress={() => setAdvFilters({})}
            hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
            style={{ paddingHorizontal: 8 }}
          >
            <Text style={{ color: '#DC2626', fontSize: 12, fontWeight: '600' }}>
              {(lang || '').startsWith('fr') ? 'Effacer' : 'Clear'}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* v1.12.345 — Modal bottom-sheet (mobile / web<768) — sur desktop la sidebar gère */}
      {!isDesktopWeb && (
      <CatalogFiltersPanel
        mode="modal"
        visible={showFilters}
        onClose={() => setShowFilters(false)}
        filters={advFilters}
        available={availableFiltersForPanel}
        onChange={(nextFilters) => {
          setAdvFilters(nextFilters);
          // Persiste les préférences en arrière-plan (best-effort)
          try {
            saveFilterPrefs({
              favorite_brands:  nextFilters.brands  || [],
              favorite_styles:  nextFilters.styles  || [],
              preferred_colors: nextFilters.colors  || [],
              price_range_preferred:
                (typeof nextFilters.price_min === 'number' || typeof nextFilters.price_max === 'number')
                  ? [nextFilters.price_min || 0, nextFilters.price_max || null]
                  : null,
            });
          } catch {}
        }}
        onReset={() => setAdvFilters({})}
        onApply={() => setShowFilters(false)}
        resultsCount={filtered.length}
        locale={lang}
        subfilterList={category !== 'Tout' ? ['Tout', ...(getSubfiltersFor(category, effectiveGender || 'N') || []).filter(s => s !== 'Tout')] : null}
        subfilterCounts={subCounts}
        currentSubfilter={subFilter}
        onSubfilterChange={(sub) => setSubFilter(sub)}
        labelForSubfilter={labelForSub}
        currentSort={sortBy}
        onSortChange={(k) => setSortBy(k)}
      />
      )}

      {/* Product grid */}
      {visibleData.length === 0 ? (
        <View style={s.empty}>
          <Text style={s.emptyIcon}>🔍</Text>
          <Text style={s.emptyTitle}>{t('no_results_found')}</Text>
          <Text style={s.emptyDesc}>
            {useAwin && search.length >= 3
              ? t('no_awin_results')
              : t('try_other_filters')}
          </Text>
        </View>
      ) : (
        <View style={{ flex: 1 }}>
          {lastOpened && (
            <TouchableOpacity
              style={s.boughtFloatingBtn}
              onPress={() => setBoughtProduct(lastOpened)}>
              <Text style={s.boughtFloatingTxt}>
                {t('bought_prefix') + ' ' + lastOpened.name.slice(0, 28) + (lastOpened.name.length > 28 ? '…' : '')}
              </Text>
              <TouchableOpacity onPress={() => setLastOpened(null)} style={{ paddingLeft: 10 }}>
                <Text style={s.boughtFloatingClose}>✕</Text>
              </TouchableOpacity>
            </TouchableOpacity>
          )}
          <FlatList
            ref={flatListRef}
            data={visibleData}
            // v1.12.539 — Key STABLE (sans index) : évite remount massif au filtrage → fix crash RCTImageManager
            keyExtractor={(item) => `cat_${item.id || item.partner_sku || (item.partner + '_' + item.name)}`}
            refreshing={refreshing}
            onRefresh={onRefresh}
            numColumns={2}
            columnWrapperStyle={{ gap: spacing.md, paddingHorizontal: spacing.xl, marginBottom: spacing.md }}
            contentContainerStyle={{ paddingTop: 6, paddingBottom: 100 }}
            showsVerticalScrollIndicator={false}
            removeClippedSubviews={Platform.OS === 'android'}
            initialNumToRender={8}
            maxToRenderPerBatch={10}
            windowSize={5}
            updateCellsBatchingPeriod={50}
            renderItem={renderProductItem}
            onScroll={(e) => {
              const y = e.nativeEvent.contentOffset.y;
              setShowScrollTop(y > 400);
            }}
            scrollEventThrottle={16}
          />

          {/* Bouton flèche "retour en haut" */}
          {showScrollTop && (
            <TouchableOpacity
              style={s.scrollTopBtn}
              onPress={scrollToTop}
              activeOpacity={0.85}
            >
              <Text style={s.scrollTopArrow}>↑</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {selected && (
        <ProductDetailModal
          product={selected}
          onClose={() => setSelected(null)}
          userId={user?.id || null}
          screen="catalog"
          onBought={(product) => {
            setSelected(null);
            setBoughtProduct(product);
          }}
        />
      )}
      {boughtProduct && (
        <PurchaseConfirmModal
          product={boughtProduct}
          onClose={() => setBoughtProduct(null)}
          onConfirmed={(item) => {
            setBoughtProduct(null);
            setLastOpened(null);
            DeviceEventEmitter.emit('wardrobe_updated', { newItem: item });
          }}
        />
      )}
        </View>{/* /colonne principale */}
      </View>{/* /wrapper responsive */}
    </SafeAreaView>
      <UpgradeModal
        visible={showPremium}
        onClose={closePremium}
        currentTier={limitInfo?.tier || 'free'}
        feature={limitInfo?.trigger || 'wardrobe'}
        usage={limitInfo ? { current: limitInfo.current, limit: limitInfo.limit, nextResetDate: limitInfo.nextResetDate, isPaidTier: limitInfo.isPaidTier } : null}
        onSubscribe={async (planId) => {
          try {
            const tier = planId.replace('_yearly', '');
            const period = planId.includes('_yearly') ? 'yearly' : 'monthly';
            const token = user?.accessToken;
            if (token) {
              await fetch(AWS_CONFIG.apiUrl + '/set-tier', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
                body: JSON.stringify({ tier, period, platform: 'test', payment_ref: 'test_' + Date.now() }),
              });
            }
            const { saveTierLocally } = require('../services/PremiumService');
            const uid = user?.id || user?.sub;
            if (uid) await saveTierLocally(uid, tier);
            Alert.alert('[TEST] Plan activé', 'Votre plan ' + tier.toUpperCase() + ' est actif.');
          } catch(e) { console.warn(e); }
        }}
      />
    </>
  );
}


/* ── SmartImage ────────────────────────────────────────── */
const ZARA_PROXIES = [
  function(url) { return 'https://wsrv.nl/?url=' + encodeURIComponent(url) + '&w=400&output=webp'; },
  function(url) { return 'https://images.weserv.nl/?url=' + encodeURIComponent(url) + '&w=400&output=webp'; },
  function(url) { return url; },
];
// Pour Cashmere : RocketCDN (bypass hotlink) en priorité, puis proxies standards
const CASHMERE_PROXIES = [
  // Réécriture vers le CDN RocketCDN que le site utilise lui-même (sert les images sans Referer check)
  function(url) {
    if (url.includes('www.cashmereboutique.com')) {
      return url.replace('www.cashmereboutique.com', '876572ee.delivery.rocketcdn.me');
    }
    return url;
  },
  function(url) { return 'https://images1-focus-opensocial.googleusercontent.com/gadgets/proxy?container=focus&url=' + encodeURIComponent(url); },
  function(url) { return 'https://images2-focus-opensocial.googleusercontent.com/gadgets/proxy?container=focus&url=' + encodeURIComponent(url); },
  function(url) { return 'https://wsrv.nl/?url=' + encodeURIComponent(url) + '&w=400'; },
  function(url) { return 'https://images.weserv.nl/?url=' + encodeURIComponent(url) + '&w=400'; },
  function(url) { return url; },
];
const OTHER_PROXIES = [
  function(url) { return url; },
  function(url) { return 'https://wsrv.nl/?url=' + encodeURIComponent(url) + '&w=400'; },
  function(url) { return 'https://images.weserv.nl/?url=' + encodeURIComponent(url) + '&w=400'; },
];

function SmartImage({ uri, style, fallbackEmoji }) {
  const [idx, setIdx] = React.useState(0);
  const [dead, setDead] = React.useState(false);
  // v1.13.43 — URI d'origine (avant normalisation) pour marquer l'image morte
  const origUri = uri;
  // v1.12.535 — Force HTTPS (Mixed Content sur web HTTPS — catalogue TOUS http://)
  if (uri && typeof uri === 'string' && uri.startsWith('http://')) {
    uri = 'https://' + uri.slice(7);
  }
  const isCashmereUri = uri && (uri.includes('cashmereboutique.com') || uri.includes('rocketcdn.me'));
  if (!uri || dead) {
    // Fallback élégant pour Cashmere : couleur camel + logo texte
    if (isCashmereUri) {
      return (
        <View style={[style, { backgroundColor: '#D4B896', alignItems: 'center', justifyContent: 'center' }]}>
          <Text style={{ fontSize: 40 }}>🧣</Text>
          <Text style={{ fontSize: 9, fontWeight: '600', color: '#8B6F47', marginTop: 4, letterSpacing: 1 }}>CASHMERE</Text>
        </View>
      );
    }
    return (
      <View style={[style, { backgroundColor: '#F5F5F5', alignItems: 'center', justifyContent: 'center' }]}>
        <Text style={{ fontSize: 28 }}>{fallbackEmoji || '👗'}</Text>
      </View>
    );
  }
  const isZara     = uri.includes('static.zara.net') || uri.includes('zstat.net');
  const isCashmere = uri.includes('cashmereboutique.com') || uri.includes('rocketcdn.me');
  const isImgProxy = uri.includes('/img-proxy'); // notre Lambda
  // Si c'est déjà notre proxy Lambda, pas besoin d'ajouter un proxy
  const proxies  = isImgProxy ? OTHER_PROXIES : (isCashmere ? CASHMERE_PROXIES : (isZara ? ZARA_PROXIES : OTHER_PROXIES));
  const fn       = proxies[idx] || proxies[0];
  const src      = fn(uri);
  // v1.12.899 — Migration expo-image : cache disque automatique + recyclage views FlatList
  // contentFit déduit du style (cs.imgContain = contain, cs.img = cover)
  // Fallback proxy chain conservé via onError → setIdx
  const contentFit = Array.isArray(style)
    ? (style.some(s => s?.resizeMode === 'contain') ? 'contain' : 'cover')
    : (style?.resizeMode === 'contain' ? 'contain' : 'cover');
  return (
    <ExpoImage
      source={{ uri: src }}
      style={style}
      contentFit={contentFit}
      cachePolicy="memory-disk"
      transition={150}
      recyclingKey={uri}
      onError={() => {
        if (idx < proxies.length - 1) setIdx(idx + 1);
        else {
          setDead(true);
          // v1.13.43 — tous les proxies ont échoué → image morte (404 confirmé) →
          // registre global → la tuile sera retirée du catalogue + l'article
          // exclu de la sélection styliste (via hasUsableImage).
          markImageDead(origUri);
        }
      }}
    />
  );
}

/* ── ProductCard ────────────────────────────────────────── */

function CatalogProductCard({ product, onPress }) {
  const [isFav, setIsFav] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    isFavorite(product.id).then(v => { if (mounted) setIsFav(v); });
    return () => { mounted = false; };
  }, [product.id]);

  const handleToggleFav = async (e) => {
    e?.stopPropagation?.();
    const wasAdded = await toggleFavorite(product);
    setIsFav(wasAdded);
  };

  const partnerColors = {
    zalando:'#FF6900', asos:'#111', hm:'#E50010', amazon:'#FF9900', zara:'#000',
    cashmere:'#8B6F47', iqueens:'#C8A560', tous:'#0A0A0A',
  };
  const pColor = partnerColors[product.partner] || '#666';
  // ═══ v1.7.81 : badge partenaire = vrai logo (PartnerLogo) au lieu d'initiales ═══
  // v1.12.297 — Suit Negozi : images plein cadre Shopify, on les affiche en 'contain'
  // pour ne pas couper les vêtements premium (Burberry, Dior, Fendi...). Autres partenaires
  // ont des images déjà cadrées → cover OK.
  // v1.12.323 + v1.12.336 — SUIT, ANN, COUTR en mode "contain" (images portrait luxe coupées sinon)
  // v1.12.886 — Ajout TAMARIS (proportions hautes chaussures) + DRESTIGE (multi-marques portrait)
  const containPartners = new Set(['suit_negozi', 'ann_demeulemeester', 'coutr', 'tamaris', 'drestige', 'desigual']);
  const imgStyle = containPartners.has(product.partner) ? cs.imgContain : cs.img;
  return (
    <TouchableOpacity style={cs.card} onPress={onPress} activeOpacity={0.85}>
      <View style={cs.imgWrap}>
        <SmartImage uri={product.image} style={imgStyle} fallbackEmoji={product.emoji || '👗'} />
        {/* v1.12.297 — utiliser comparaison stricte > 0 sinon React rend "0" littéral si discount=0 */}
        {product.discount > 0 && (
          <View style={cs.discBadge}><Text style={cs.discTxt}>-{product.discount}%</Text></View>
        )}
        {product.isTrending && (
          <View style={[cs.trendBadgeWrap, product.discount && cs.trendBadgeBelowDisc]}>
            <TrendingBadge tag={product.trendTag} compact />
          </View>
        )}
        <View style={[cs.partnerBadge, { backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: pColor + '40' }]}>
          <PartnerLogo partner={product.partner} size={14} />
        </View>
        {/* Bouton cœur favoris */}
        <TouchableOpacity
          style={cs.heartBtn}
          onPress={handleToggleFav}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Text style={cs.heartIcon}>{isFav ? '❤️' : '🤍'}</Text>
        </TouchableOpacity>
      </View>
      <View style={cs.info}>
        <Text style={cs.brand} numberOfLines={1}>{product.brand}</Text>
        <Text style={cs.name}  numberOfLines={2}>{product.name}</Text>
        <View style={cs.priceRow}>
          {/* v1.12.336 — affichage devise par produit ($ pour COUTR, € sinon) */}
          {(() => {
            const isUsd = product.partner === 'coutr' || product.currency === 'USD';
            return (
              <>
                <Text style={cs.price}>{isUsd ? `$${product.price}` : `${product.price} €`}</Text>
                {product.originalPrice > product.price + 0.5 && (
                  <Text style={cs.origPrice}>{isUsd ? `$${product.originalPrice}` : `${product.originalPrice} €`}</Text>
                )}
              </>
            );
          })()}
        </View>
      </View>
    </TouchableOpacity>
  );
}

const cs = StyleSheet.create({
  card:         { flex:1, backgroundColor:'#fff', borderRadius:10, borderWidth:1, borderColor:'#EFEFEF', overflow:'hidden', margin:2 },
  imgWrap:      { width:'100%', height:200, position:'relative' },
  img:          { width:'100%', height:'100%', resizeMode:'cover' },
  // v1.12.297 — variant 'contain' (Suit Negozi : ne pas couper l'article)
  imgContain:   { width:'100%', height:'100%', resizeMode:'contain', backgroundColor:'#FAFAFA' },
  discBadge:    { position:'absolute', top:6, left:6, backgroundColor:'#E53935', paddingHorizontal:6, paddingVertical:2, borderRadius:4 },
  discTxt:      { fontSize:10, fontWeight:'700', color:'#fff' },
  trendBadgeWrap:     { position:'absolute', top:6, left:6, zIndex:2 },
  trendBadgeBelowDisc:{ top:32 },
  partnerBadge: { position:'absolute', top:6, right:38, width:24, height:24, borderRadius:12, alignItems:'center', justifyContent:'center' },
  partnerTxt:   { fontSize:8, color:'#fff', fontWeight:'700' },
  heartBtn:     { position:'absolute', top:6, right:6, width:28, height:28, borderRadius:14, backgroundColor:'rgba(255,255,255,0.92)', alignItems:'center', justifyContent:'center', shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.15, shadowRadius:2, elevation:2 },
  heartIcon:    { fontSize:14 },
  info:         { padding:8, gap:2 },
  brand:        { fontSize:9, letterSpacing:1.5, color:'#999', textTransform:'uppercase' },
  name:         { fontSize:12, fontWeight:'500', color:'#111', lineHeight:16 },
  priceRow:     { flexDirection:'row', alignItems:'center', gap:6, marginTop:2 },
  price:        { fontSize:14, fontWeight:'700', color:'#111' },
  origPrice:    { fontSize:11, color:'#999', textDecorationLine:'line-through' },
});

// v1.12.255 — Search Agent v2 styles
const searchStyles = StyleSheet.create({
  bar: {
    flexDirection: 'row', alignItems: 'center',
    marginHorizontal: 12, marginTop: 6, marginBottom: 2,
    paddingHorizontal: 12, height: 38,
    borderRadius: 10, borderWidth: 1,
    gap: 8,
  },
  icon: { fontSize: 14, opacity: 0.6 },
  input: { flex: 1, fontSize: 14, paddingVertical: 0 },
  clear: { fontSize: 14, color: '#A89E94', paddingHorizontal: 4 },
  typoBanner: {
    marginHorizontal: 12, marginBottom: 2,
    paddingVertical: 6, paddingHorizontal: 10,
    backgroundColor: '#FFF7E0',
    borderWidth: 1, borderColor: '#D4AF72',
    borderRadius: 8,
  },
  typoText: { fontSize: 11, color: '#B8860B', fontWeight: '600' },
  chipsRow: { gap: 4, paddingHorizontal: 12, paddingBottom: 6, paddingTop: 4, alignItems: 'center' },
  chipsInline: { gap: 4, alignItems: 'center', paddingRight: 4 },
  chip: {
    flexDirection: 'row', alignItems: 'center', gap: 3,
    paddingHorizontal: 7, paddingVertical: 2,
    backgroundColor: '#1A1814',
    borderRadius: 9,
    maxWidth: 110,
  },
  chipText: { fontSize: 10, color: '#FFFFFF', fontWeight: '600', flexShrink: 1 },
  chipX: { fontSize: 9, color: '#D4AF72', fontWeight: '700' },
  chipMore: {
    paddingHorizontal: 8, paddingVertical: 3,
    borderRadius: 10,
    backgroundColor: '#FBF7EE',
    borderWidth: 1, borderColor: '#D4AF72',
  },
  chipMoreText: { fontSize: 10, color: '#B8860B', fontWeight: '700' },
  autoBox: {
    marginHorizontal: 12, marginTop: 2, marginBottom: 4,
    backgroundColor: '#FFFFFF',
    borderRadius: 12, borderWidth: 1, borderColor: '#EAE6E0',
    overflow: 'hidden',
    shadowColor: '#000', shadowOpacity: 0.06, shadowRadius: 8, shadowOffset: { width: 0, height: 2 },
  },
  autoItem: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 10, paddingHorizontal: 12,
    borderBottomWidth: 1, borderBottomColor: '#F5F2ED',
  },
  autoText: { flex: 1, fontSize: 13, color: '#1A1814' },
  autoType: { fontSize: 9, color: '#A89E94', textTransform: 'uppercase', letterSpacing: 0.4 },
  trendingBox: {
    marginHorizontal: 12, marginTop: 2, marginBottom: 4,
    backgroundColor: '#FBF7EE',
    borderRadius: 12, borderWidth: 1, borderColor: 'rgba(212,175,114,0.4)',
    paddingTop: 8,
  },
  trendingTitle: { fontSize: 10, fontWeight: '700', color: '#B8860B', letterSpacing: 1, paddingHorizontal: 12, paddingBottom: 6 },
});


const s = StyleSheet.create({
  safe:              { flex: 1, backgroundColor: '#FFFFFF' },
  header:            { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', paddingHorizontal: spacing.xl, paddingTop: spacing.md, paddingBottom: 6 },
  title:             { fontSize: 20, fontWeight: '300', color: colors.text.primary, letterSpacing: 0.5 },
  subtitleRow:       { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 },
  subtitle:          { fontSize: 11, color: colors.text.faint },
  awinBadge:         { flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: '#E1F5EE', borderRadius: 8, paddingHorizontal: 6, paddingVertical: 2 },
  awinDot:           { width: 5, height: 5, borderRadius: 3, backgroundColor: '#1D9E75' },
  awinText:          { fontSize: 9, fontWeight: '600', color: '#085041' },
  headerRight:       { flexDirection: 'row', alignItems: 'center', gap: 10 },
  outfitBadgeBtn:    { backgroundColor: colors.accent.ghost, borderRadius: radius.pill, borderWidth: 1, borderColor: colors.accent.pale, paddingHorizontal: 10, paddingVertical: 5 },
  outfitBadgeText:   { fontSize: 11, fontWeight: '600', color: colors.accent.dark },
  cartBtn:           { width: 38, height: 38, alignItems: 'center', justifyContent: 'center', position: 'relative' },
  cartIcon:          { fontSize: 20 },
  cartBadge:         { position: 'absolute', top: 0, right: 0, width: 16, height: 16, borderRadius: 8, backgroundColor: colors.status.error, alignItems: 'center', justifyContent: 'center', borderWidth: 1.5, borderColor: '#fff' },
  cartBadgeText:     { fontSize: 8, color: '#fff', fontWeight: '700' },
  tabs:              { flexDirection: 'row', paddingHorizontal: spacing.xl, gap: spacing.xl },
  tab:               { paddingBottom: spacing.sm },
  tabActive:         { borderBottomWidth: 2, borderBottomColor: colors.text.primary },
  tabLabel:          { fontSize: 13, color: colors.text.faint },
  tabLabelActive:    { color: colors.text.primary, fontWeight: '500' },
  divider:           { height: 1, backgroundColor: colors.bg.border },
  searchRow:   { flexDirection: 'row', gap: 8, paddingHorizontal: spacing.xl, paddingTop: 6, paddingBottom: 0 },
  searchBox:         { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: colors.bg.surface, borderRadius: radius.md, borderWidth: 1, borderColor: colors.bg.border, paddingHorizontal: spacing.md, height: 42 },
  searchIcon:        { fontSize: 14, marginRight: 6 },
  searchInput:       { flex: 1, fontSize: 13, color: colors.text.primary },
  clearBtn:          { fontSize: 12, color: colors.text.faint, padding: 4 },
  filterBtn:         { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, height: 42, borderRadius: radius.md, borderWidth: 1.5, borderColor: colors.text.primary, backgroundColor: colors.bg.surface, position: 'relative' },
  filterBtnActive:   { backgroundColor: colors.text.primary, borderColor: colors.text.primary },
  filterBtnIcon:     { fontSize: 14, color: colors.text.primary, fontWeight: '700' },
  filterBtnIconActive: { color: '#FFF' },
  filterBtnLabel:    { fontSize: 13, color: colors.text.primary, fontWeight: '600' },
  filterBtnLabelActive: { color: '#FFF' },
  filterBadge:       { position: 'absolute', top: -6, right: -6, minWidth: 18, height: 18, borderRadius: 9, backgroundColor: '#E53935', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 4, borderWidth: 2, borderColor: '#FFF' },
  filterBadgeText:   { color: '#FFF', fontSize: 10, fontWeight: '700' },
  catScrollWrap: { flexShrink: 0 },
  catRow:      { paddingHorizontal: spacing.xl, gap: 8, paddingTop: 4, paddingBottom: 8 },

  // ═══ v1.12.20 : Sous-filtres rapides (Chemises, T-shirts, Sneakers...) ═══
  subfilterScrollWrap: {
    flexShrink: 0,
    paddingTop: 2,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.04)',
  },
  subfilterRow: {
    paddingHorizontal: spacing.xl,
    gap: 6,
    paddingVertical: 4,
  },
  subfilterChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: radius.pill,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.10)',
  },
  subfilterChipActive: {
    backgroundColor: colors.text.primary,
    borderColor: colors.text.primary,
  },
  subfilterLabel: {
    fontSize: 12,
    color: colors.text.faint,
    fontWeight: '500',
  },
  subfilterLabelActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  subfilterLabelEmpty: {
    color: 'rgba(0,0,0,0.30)',
  },
  // v1.12.23 : compteur affiché à côté du label dans chaque chip sous-filtre
  subfilterCount: {
    fontSize: 11,
    fontWeight: '400',
    color: 'rgba(0,0,0,0.45)',
  },
  subfilterCountActive: {
    color: 'rgba(255,255,255,0.75)',
  },
  subfilterCountEmpty: {
    color: 'rgba(0,0,0,0.25)',
  },

  // ═══ v1.7.76 : Filtre partenaires premium (au-dessus catégories) ═══
  partnerScrollWrap: { flexShrink: 0, paddingTop: 6, paddingBottom: 4, borderBottomWidth: 1, borderBottomColor: 'rgba(0,0,0,0.04)' },
  partnerRow: { paddingHorizontal: spacing.xl, gap: 8, paddingVertical: 6 },
  partnerChip: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 14, paddingVertical: 7,
    borderRadius: radius.pill,
    backgroundColor: '#FFFFFF',
    borderWidth: 1, borderColor: '#E8E2D5',
  },
  partnerChipActive: {
    backgroundColor: '#0A0A0A',
    borderColor: '#D4AF37', // accent doré pour signaler "premium"
    shadowColor: '#D4AF37', shadowOpacity: 0.3, shadowRadius: 4, shadowOffset: { width: 0, height: 1 },
    elevation: 2,
  },
  partnerEmoji: { fontSize: 13 },
  partnerLabel: { fontSize: 12, color: '#5C5343', fontWeight: '600', letterSpacing: 0.3 },
  partnerLabelActive: { color: '#F4E4A1' }, // ton doré clair sur fond noir

  // ═══ v1.7.83 : Bannière diagnostic TOUS (visible si problème) ═══
  tousDebugBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    marginHorizontal: spacing.xl,
    marginTop: 10,
    marginBottom: 6,
    padding: 12,
    backgroundColor: '#FFF3E0',
    borderLeftWidth: 3,
    borderLeftColor: '#FB8C00',
    borderRadius: radius.md,
  },
  tousDebugIcon: { fontSize: 18, marginTop: 1 },
  tousDebugTitle: { fontSize: 13, fontWeight: '700', color: '#5C3A00', marginBottom: 4 },
  tousDebugText: { fontSize: 11, color: '#7A4A00', lineHeight: 15 },
  tousDebugSample: { fontSize: 10, color: '#5C3A00', marginTop: 4, fontFamily: 'Menlo' },
  tousDebugAction: { fontSize: 10, fontWeight: '700', color: '#B85F00', marginTop: 6 },

  catChip:     { paddingHorizontal: 14, paddingVertical: 8, borderRadius: radius.pill, borderWidth: 1.5, borderColor: colors.bg.border, backgroundColor: '#FFFFFF' },
  catChipActive: { borderColor: colors.text.primary, backgroundColor: colors.text.primary },
  catLabel:    { fontSize: 11, letterSpacing: 0.5, color: colors.text.secondary, fontWeight: '500' },
  catLabelActive: { color: '#fff' },

  // Catalog premium chip (style harmonisé avec Dressing)
  catChipPremium:      { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 8, borderRadius: radius.pill, backgroundColor: '#F5F3EE', borderWidth: 1, borderColor: 'transparent' },
  catChipPremiumActive:{ backgroundColor: colors.text.primary, borderColor: colors.text.primary },
  catLabelPremium:     { fontSize: 12, color: colors.text.secondary, fontWeight: '500', letterSpacing: 0.2 },
  catLabelPremiumActive:{ color: '#FFF', fontWeight: '600' },
  catCountBubblePrem:      { minWidth: 20, height: 18, borderRadius: 9, backgroundColor: '#FFF', paddingHorizontal: 5, alignItems: 'center', justifyContent: 'center' },
  catCountBubblePremActive:{ backgroundColor: 'rgba(255,255,255,0.25)' },
  catCountBubbleEmpty:     { backgroundColor: '#F0F0F0' },
  catCountTextPrem:        { fontSize: 10, color: colors.text.primary, fontWeight: '700' },
  catCountTextPremActive:  { color: '#FFF' },
  catCountTextEmpty:       { color: '#BBB', fontWeight: '500' },

  // Bouton flèche "retour en haut" (flottant)
  scrollTopBtn: {
    position: 'absolute',
    bottom: 30, right: 20,
    width: 48, height: 48, borderRadius: 24,
    backgroundColor: '#1A1A1A',
    alignItems: 'center', justifyContent: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25, shadowRadius: 8,
    elevation: 8,
    borderWidth: 1, borderColor: '#D4AF72',
  },
  scrollTopArrow: { fontSize: 22, color: '#D4AF72', fontWeight: '700', lineHeight: 24 },

  // Header catalogue badge
  catalogBadge:        { fontSize: 9, letterSpacing: 2.5, fontWeight: '700', marginBottom: 3 },
  subtitleDot:         { fontSize: 11, color: colors.text.faint, marginHorizontal: 4 },
  awinBar:           { marginHorizontal: spacing.xl, marginBottom: 4, backgroundColor: '#E1F5EE', borderRadius: radius.sm, paddingHorizontal: 10, paddingVertical: 4 },
  awinBarText:       { fontSize: 10, color: '#085041' },
  filterPanel:       { backgroundColor: colors.bg.surface, borderTopWidth: 1, borderBottomWidth: 1, borderColor: colors.bg.border, paddingHorizontal: spacing.xl, paddingVertical: 8, gap: 8 },
  filterSection:     { gap: 6 },
  filterLabel:       { fontSize: 9, letterSpacing: 2.5, color: colors.text.faint, textTransform: 'uppercase' },
  filterChips:       { flexDirection: 'row', flexWrap: 'wrap', gap: 5, marginTop: 6 },
  smallChip:         { paddingHorizontal: 10, paddingVertical: 4, borderRadius: radius.pill, borderWidth: 1, borderColor: colors.bg.border },
  smallChipActive:   { backgroundColor: colors.text.primary, borderColor: colors.text.primary },
  smallChipText:     { fontSize: 10, color: colors.text.muted },
  smallChipTextActive:{ color: '#fff' },
  filterToggle:      { flexDirection: 'row', alignItems: 'center', gap: 8 },
  toggle:            { width: 36, height: 20, borderRadius: 10, backgroundColor: colors.bg.border },
  toggleOn:          { backgroundColor: colors.status.success },
  filterToggleLabel: { fontSize: 12, color: colors.text.secondary },
  resetBtn:           { marginTop: 8, alignSelf: 'flex-start', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6, backgroundColor: '#FEE' },
  resetBtnText:       { fontSize: 12, color: '#C53030', fontWeight: '600' },
  card:              { backgroundColor: '#fff', borderRadius: radius.md, borderWidth: 1, borderColor: colors.bg.border, overflow: 'hidden' },
  cardImg:           { height: CARD_W * 1.15, backgroundColor: colors.bg.surface, position: 'relative', alignItems: 'center', justifyContent: 'center' },
  cardImgPhoto:      { width: '100%', height: '100%', resizeMode: 'cover' },
  cardEmoji:         { fontSize: 48 },
  awinProductBadge:  { position: 'absolute', top: 8, left: 8, backgroundColor: '#085041', borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2 },
  awinProductBadgeText:{ fontSize: 8, color: '#fff', fontWeight: '700' },
  discountBadge:     { position: 'absolute', top: 8, left: 8, backgroundColor: colors.status.error, borderRadius: radius.pill, paddingHorizontal: 7, paddingVertical: 3 },
  discountText:      { fontSize: 9, color: '#fff', fontWeight: '700' },
  outfitToggle:      { position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: 14, backgroundColor: 'rgba(255,255,255,0.9)', alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: colors.bg.border },
  outfitToggleActive:{ backgroundColor: colors.accent.ghost, borderColor: colors.accent.main },
  outfitToggleText:  { fontSize: 14, color: colors.text.primary, lineHeight: 16 },
  partnerDotCard:    { position: 'absolute', bottom: 8, right: 8, width: 8, height: 8, borderRadius: 4 },
  cardInfo:          { padding: spacing.sm },
  cardBrand:         { fontSize: 9, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase', marginBottom: 2 },
  cardName:          { fontSize: 12, color: colors.text.primary, lineHeight: 16, marginBottom: 5 },
  cardBottom:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  cardPrice:         { fontSize: 14, fontWeight: '600', color: colors.text.primary },
  cardOriginalPrice: { fontSize: 10, color: colors.text.ghost, textDecorationLine: 'line-through' },
  cartBtnSmall:      { width: 28, height: 28, borderRadius: 14, backgroundColor: colors.text.primary, alignItems: 'center', justifyContent: 'center' },
  cartBtnSmallDone:  { backgroundColor: colors.status.success },
  cartBtnSmallText:  { fontSize: 16, color: '#fff', lineHeight: 18 },
  cartBtnSmallTextDone:{ fontSize: 12, lineHeight: 14 },
  empty:             { flex: 1, alignItems: 'center', justifyContent: 'center', paddingTop: 60, gap: 8 },
  emptyIcon:         { fontSize: 36 },
  emptyTitle:        { fontSize: 16, fontWeight: '300', color: colors.text.primary },
  emptyDesc:         { fontSize: 12, color: colors.text.faint },
  modalSafe:         { flex: 1, backgroundColor: '#fff' },
  modalHeader:       { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: spacing.xl, paddingVertical: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.bg.border },
  modalClose:        { width: 32, height: 32, borderRadius: 16, backgroundColor: colors.bg.surface, borderWidth: 1, borderColor: colors.bg.border, alignItems: 'center', justifyContent: 'center' },
  modalCloseText:    { fontSize: 12, color: colors.text.faint },
  awinModalBadge:    { backgroundColor: '#E1F5EE', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4 },
  awinModalBadgeText:{ fontSize: 10, color: '#085041', fontWeight: '600' },
  outfitBtn:         { paddingHorizontal: 12, paddingVertical: 7, borderRadius: radius.pill, borderWidth: 1.5, borderColor: colors.bg.border },
  outfitBtnActive:   { borderColor: colors.accent.main, backgroundColor: colors.accent.ghost },
  outfitBtnText:     { fontSize: 12, color: colors.text.muted, fontWeight: '500' },
  outfitBtnTextActive:{ color: colors.accent.dark },
  modalImg:          { height: 340, backgroundColor: colors.bg.surface, alignItems: 'center', justifyContent: 'center', position: 'relative' },
  modalImgPhoto:     { width: '100%', height: '100%', resizeMode: 'cover' },
  modalImgEmoji:     { fontSize: 80 },
  discountBadgeLg:   { position: 'absolute', top: 16, left: 16, backgroundColor: colors.status.error, borderRadius: radius.pill, paddingHorizontal: 10, paddingVertical: 5 },
  discountTextLg:    { fontSize: 12, color: '#fff', fontWeight: '700' },
  modalContent:      { padding: spacing.xl, gap: spacing.lg },
  partnerRow:        { flexDirection: 'row', alignItems: 'center', gap: 8, borderRadius: radius.sm, padding: spacing.sm },
  partnerRowLogo:    { fontSize: 16 },
  partnerRowName:    { fontSize: 12, fontWeight: '600' },
  partnerRowDelivery:{ flex: 1, fontSize: 11, color: colors.text.faint, textAlign: 'right' },
  modalTitleRow:     { flexDirection: 'row', gap: spacing.md },
  modalBrand:        { fontSize: 11, letterSpacing: 2, color: colors.text.faint, textTransform: 'uppercase', marginBottom: 4 },
  modalName:         { fontSize: 20, fontWeight: '300', color: colors.text.primary, lineHeight: 26 },
  modalPrice:        { fontSize: 22, fontWeight: '600', color: colors.text.primary },
  modalOriginalPrice:{ fontSize: 13, color: colors.text.ghost, textDecorationLine: 'line-through', textAlign: 'right' },
  modalSection:      { gap: 8 },
  modalSectionTitle: { fontSize: 11, letterSpacing: 2, color: colors.text.faint, textTransform: 'uppercase' },
  modalDesc:         { fontSize: 13, color: colors.text.secondary, lineHeight: 20 },
  sizesRow:          { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  sizeChip:          { paddingHorizontal: 14, paddingVertical: 8, borderRadius: radius.sm, borderWidth: 1.5, borderColor: colors.bg.border },
  sizeChipActive:    { borderColor: colors.text.primary, backgroundColor: colors.text.primary },
  sizeChipText:      { fontSize: 13, color: colors.text.secondary },
  sizeChipTextActive:{ color: '#fff', fontWeight: '500' },
  modalFooter:       { borderTopWidth: 1, borderTopColor: colors.bg.border, padding: spacing.xl },
  modalFooterRow:    { flexDirection: 'row', gap: 8 },
  footerBtn:         { paddingVertical: 14, borderRadius: radius.md, alignItems: 'center', justifyContent: 'center' },
  footerBtnDark:     { backgroundColor: colors.text.primary },
  footerBtnOutline:  { borderWidth: 1.5, borderColor: colors.bg.border, backgroundColor: '#fff' },
  footerBtnSuccess:  { backgroundColor: colors.status.success },
  footerBtnGreen:    { backgroundColor: '#2e7d32' },
  footerBtnTxt:      { fontSize: 13, fontWeight: '600', color: '#fff' },
  footerBtnTxtDark:  { fontSize: 13, fontWeight: '600', color: colors.text.primary },
  addCTA:            { backgroundColor: colors.text.primary, borderRadius: radius.md, paddingVertical: 15, alignItems: 'center' },
  addCTAText:        { fontSize: 14, fontWeight: '600', color: '#fff', letterSpacing: 0.5 },
  externalBtn:       { width: 52, height: 52, borderRadius: radius.md, borderWidth: 1.5, borderColor: colors.bg.border, alignItems: 'center', justifyContent: 'center' },
  externalBtnText:   { fontSize: 13, color: colors.text.primary, fontWeight: '500' },
  outfitEmpty:       { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.xl, gap: spacing.md },
  outfitEmptyIcon:   { fontSize: 40, color: colors.accent.light },
  outfitEmptyTitle:  { fontSize: 18, fontWeight: '300', color: colors.text.primary },
  outfitEmptyDesc:   { fontSize: 13, color: colors.text.faint, textAlign: 'center' },
  outfitItem:        { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: '#fff', borderRadius: radius.md, borderWidth: 1, borderColor: colors.bg.border, padding: spacing.md, marginBottom: 8 },
  outfitItemImg:     { width: 56, height: 66, borderRadius: radius.sm, backgroundColor: colors.bg.surface, overflow: 'hidden', alignItems: 'center', justifyContent: 'center' },
  outfitItemPhoto:   { width: '100%', height: '100%', resizeMode: 'cover' },
  outfitItemEmoji:   { fontSize: 24 },
  outfitItemInfo:    { flex: 1 },
  outfitItemName:    { fontSize: 13, fontWeight: '500', color: colors.text.primary, marginBottom: 2 },
  outfitItemBrand:   { fontSize: 10, color: colors.text.faint, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 },
  outfitPartnerPill: { alignSelf: 'flex-start', borderRadius: radius.pill, paddingHorizontal: 7, paddingVertical: 2 },
  outfitPartnerText: { fontSize: 9, fontWeight: '600' },
  outfitItemRight:   { alignItems: 'flex-end', gap: 8 },
  outfitItemPrice:   { fontSize: 14, fontWeight: '600', color: colors.text.primary },
  removeBtnText:     { fontSize: 11, color: colors.text.ghost },
  outfitCTA:         { backgroundColor: '#fff', borderRadius: radius.lg, borderWidth: 1, borderColor: colors.bg.border, padding: spacing.lg, marginTop: spacing.lg },
  outfitCTATop:      { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
  outfitCTALabel:    { fontSize: 11, letterSpacing: 2, color: colors.text.faint, textTransform: 'uppercase' },
  outfitCTATotal:    { fontSize: 22, fontWeight: '300', color: colors.text.primary },
  outfitCTABtn:      { backgroundColor: colors.text.primary, borderRadius: radius.sm, paddingVertical: 14, alignItems: 'center' },
  outfitCTABtnText:  { fontSize: 13, fontWeight: '600', color: '#fff', letterSpacing: 0.5 },
  matchBadge:        { position: 'absolute', top: 8, left: 8, backgroundColor: '#C9A84C', borderRadius: 6, paddingHorizontal: 6, paddingVertical: 3 },
  matchBadgeText:    { fontSize: 8, color: '#fff', fontWeight: '700' },
  zalandoBadge:      { position: 'absolute', top: 8, left: 8, backgroundColor: '#FF6900', borderRadius: 6, paddingHorizontal: 6, paddingVertical: 3 },
  zalandoBadgeText:  { fontSize: 8, color: '#fff', fontWeight: '700' },
});
